// src/pages/StaffPortalPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addDoc, collection, doc, getDoc, getDocs, onSnapshot, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { db, storage, functions } from "../firebase";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { useTheme } from "../theme/ThemeProvider";
import { ensureStaffSession, getStaffSession } from "../utils/staffSession";
import { buildPayrollSummary, dedupePayrollRows, getDisplayClockOutSec, toSec, computePaidHours } from "../utils/payrollShiftEngine";

const DEFAULT_MONTHLY_TARGET = 180;
const DEFAULT_ABANDONED_SHIFT_HOURS = 18;
const DEFAULT_LUNCH_MINUTES = 60;

const tsToDate = (value) =>
  value?.toDate
    ? value.toDate()
    : value?.seconds
    ? new Date(value.seconds * 1000)
    : value
    ? new Date(value)
    : null;


const clampLiveShiftHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_ABANDONED_SHIFT_HOURS;
  return Math.min(24, Math.max(18, Math.round(n)));
};

const isShiftClosedForPortal = (shift) =>
  Boolean(
    tsToDate(shift?.clockOut) ||
      tsToDate(shift?.actualClockOut) ||
      tsToDate(shift?.thresholdClockOut) ||
      shift?.active === false
  );

const safeHours = (start, end = new Date()) => {
  const from = tsToDate(start);
  const to = tsToDate(end);
  if (!from || !to) return 0;
  const ms = to.getTime() - from.getTime();
  return ms > 0 ? ms / 3600000 : 0;
};

const getShiftLunchMinutes = (shift, fallbackMinutes = DEFAULT_LUNCH_MINUTES) => {
  const direct = Number(shift?.lunchMin ?? shift?.lunchMinutes ?? shift?.lunch);
  if (Number.isFinite(direct) && direct >= 0) return direct;
  if (typeof shift?.lunchTaken === "boolean") return shift.lunchTaken ? fallbackMinutes : 0;
  return fallbackMinutes;
};

const getShiftRawHours = (shift, end = null) => {
  const start = tsToDate(shift?.clockIn);
  const finish = tsToDate(end ?? shift?.clockOut ?? shift?.actualClockOut ?? shift?.thresholdClockOut);
  if (!start || !finish) return 0;
  let diff = finish.getTime() - start.getTime();
  if (diff < 0) diff += 24 * 3600000;
  return diff > 0 ? diff / 3600000 : 0;
};

const getShiftPaidHours = (shift, fallbackLunchMinutes = DEFAULT_LUNCH_MINUTES) => {
  const clockOut = tsToDate(shift?.clockOut);
  if (!clockOut) return 0;
  const rawHours = getShiftRawHours(shift, clockOut);
  const lunchHours = getShiftLunchMinutes(shift, fallbackLunchMinutes) / 60;
  return Math.max(0, rawHours - lunchHours);
};

const getShiftEffectiveClockOutForTotals = (
  shift,
  now,
  abandonedShiftHours = DEFAULT_ABANDONED_SHIFT_HOURS
) => {
  const clockIn = tsToDate(shift?.clockIn);
  const realClockOut = tsToDate(shift?.clockOut);
  const actualClockOut = tsToDate(shift?.actualClockOut);
  const thresholdClockOut = tsToDate(shift?.thresholdClockOut);

  if (!clockIn) return null;
  if (realClockOut) return realClockOut;
  if (actualClockOut) return actualClockOut;
  if (thresholdClockOut) return thresholdClockOut;

  const thresholdHours = clampLiveShiftHours(abandonedShiftHours);

  const thresholdEnd = new Date(clockIn.getTime() + thresholdHours * 3600000);

  return now >= thresholdEnd ? thresholdEnd : null;
};

const withEffectiveClockOutForTotals = (
  shift,
  now,
  abandonedShiftHours = DEFAULT_ABANDONED_SHIFT_HOURS,
  defaultLunchMinutes = DEFAULT_LUNCH_MINUTES
) => {
  const effectiveClockOut = getShiftEffectiveClockOutForTotals(
    shift,
    now,
    abandonedShiftHours
  );

  if (!effectiveClockOut) return null;

  return {
    ...shift,
    clockOut: effectiveClockOut,
    lunchMin:
      Number.isFinite(Number(shift?.lunchMin ?? shift?.lunchMinutes ?? shift?.lunch))
        ? Number(shift?.lunchMin ?? shift?.lunchMinutes ?? shift?.lunch)
        : typeof shift?.lunchTaken === "boolean"
        ? shift.lunchTaken
          ? defaultLunchMinutes
          : 0
        : defaultLunchMinutes,
  };
};

const isWithinAbandonedThreshold = (shift, now, abandonedShiftHours = DEFAULT_ABANDONED_SHIFT_HOURS) => {
  const clockIn = tsToDate(shift?.clockIn);
  if (!clockIn || isShiftClosedForPortal(shift)) return false;
  const ageHours = safeHours(clockIn, now);
  return ageHours <= clampLiveShiftHours(abandonedShiftHours);
};

const getPortalOpenShiftState = (shifts, now, abandonedShiftHours = DEFAULT_ABANDONED_SHIFT_HOURS) => {
  const openShifts = [...(shifts || [])]
    .filter((shift) => tsToDate(shift?.clockIn) && !isShiftClosedForPortal(shift))
    .sort((a, b) => (tsToDate(b.clockIn)?.getTime?.() || 0) - (tsToDate(a.clockIn)?.getTime?.() || 0));

  const latestLiveOpenShift =
    openShifts.find((shift) => isWithinAbandonedThreshold(shift, now, abandonedShiftHours)) || null;

  const staleOpenShifts = openShifts.filter((shift) => !latestLiveOpenShift || shift.id !== latestLiveOpenShift.id);

  return { openShifts, latestLiveOpenShift, staleOpenShifts };
};

const dedupePortalShifts = (items, abandonedShiftHours = DEFAULT_ABANDONED_SHIFT_HOURS) => {
  return dedupePayrollRows(items || [], (shift) =>
    getDisplayClockOutSec(shift, abandonedShiftHours)
  ).sort(
    (a, b) => (tsToDate(b.clockIn)?.getTime?.() || 0) - (tsToDate(a.clockIn)?.getTime?.() || 0)
  );
};

const roundHours = (value) => Math.round((Number(value) || 0) * 10) / 10;

const isArchivedRecord = (item) => {
  if (!item) return false;
  return item.archived === true || item.isArchived === true;
};

const isSameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + diff);
  return d;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);

const formatHours = (value) => {
  const n = roundHours(value);
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const formatMinutesLabel = (value) => {
  const n = Math.max(0, Math.floor(Number(value) || 0));
  return `${n} min`;
};

const portalDateKey = (date = new Date()) => {
  const d = tsToDate(date) || new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const portalMonthKey = (date = new Date()) => {
  const d = tsToDate(date) || new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const scheduleWindowForPortal = (entry = {}) => {
  const published = String(entry.publishedShift || entry.shift || "").trim();
  const parsed = published.match(/^(\d{2}:\d{2})-(\d{2}:\d{2})$/);
  return {
    startTime: String(entry.startTime || parsed?.[1] || ""),
    endTime: String(entry.endTime || parsed?.[2] || ""),
  };
};

const scheduleDateTimeForPortal = (dateKey, timeText) => {
  const dateParts = String(dateKey || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const timeParts = String(timeText || "").match(/^(\d{2}):(\d{2})$/);
  if (!dateParts || !timeParts) return null;
  return new Date(
    Number(dateParts[1]),
    Number(dateParts[2]) - 1,
    Number(dateParts[3]),
    Number(timeParts[1]),
    Number(timeParts[2]),
    0,
    0
  );
};

const attendanceHoursForPortal = (entry = {}, defaultLunchMinutes = DEFAULT_LUNCH_MINUTES) => {
  const numericCandidates = [
    entry.allocatedHours, entry.paidHours, entry.scheduledHours, entry.hours,
    entry.durationHours, entry.leaveHours, entry.attendanceHours,
  ];
  const direct = numericCandidates.map(Number).find((value) => Number.isFinite(value) && value >= 0);
  if (direct !== undefined) return direct;

  const { startTime, endTime } = scheduleWindowForPortal(entry);
  let start = scheduleDateTimeForPortal(entry.date, startTime);
  let end = scheduleDateTimeForPortal(entry.date, endTime);
  if (!start || !end) return 0;
  if (end <= start) end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
  const raw = Math.max(0, (end.getTime() - start.getTime()) / 3600000);
  return Math.max(0, raw - Math.max(0, Number(defaultLunchMinutes) || 0) / 60);
};

const isPaidAttendanceForPortal = (entry = {}) => {
  const identity = `${entry.attendanceTypeId || ""} ${entry.attendanceLabel || ""} ${entry.leaveLabel || ""}`.toLowerCase();
  if (identity.includes("unpaid")) return false;
  if (entry.paid === false || entry.isPaid === false) return false;
  return identity.includes("leave") || entry.paid === true || entry.isPaid === true;
};

const buildScheduleVariance = ({
  entries = [],
  shifts = [],
  now = new Date(),
  defaultLunchMinutes = DEFAULT_LUNCH_MINUTES,
  abandonedShiftHours = DEFAULT_ABANDONED_SHIFT_HOURS,
  activeShiftId = "",
}) => {
  let scheduledHoursToDate = 0;
  let totalPublishedScheduledHours = 0;
  let paidLeaveHoursToDate = 0;
  let creditedWorkedHoursToDate = 0;
  let approvedOvertimeHoursToDate = 0;
  let todayScheduledHours = 0;
  let todayCreditedWorkedHours = 0;
  let todayPaidLeaveHours = 0;
  const todayKey = portalDateKey(now);
  const scheduleByDate = new Map();

  (entries || []).forEach((entry) => {
    const dateKey = String(entry?.date || "");
    if (dateKey) scheduleByDate.set(dateKey, entry);

    if (entry?.isAttendance === true || entry?.attendanceTypeId) {
      if (!dateKey || !isPaidAttendanceForPortal(entry)) return;
      const leaveHours = attendanceHoursForPortal(entry, defaultLunchMinutes);
      totalPublishedScheduledHours += leaveHours;
      if (dateKey > todayKey) return;
      scheduledHoursToDate += leaveHours;
      paidLeaveHoursToDate += leaveHours;
      if (dateKey === todayKey) {
        todayScheduledHours += leaveHours;
        todayPaidLeaveHours += leaveHours;
      }
      return;
    }
    if (entry?.off === true || String(entry?.publishedShift || "").trim().toUpperCase() === "OFF") return;

    const { startTime, endTime } = scheduleWindowForPortal(entry);
    let start = scheduleDateTimeForPortal(dateKey, startTime);
    let end = scheduleDateTimeForPortal(dateKey, endTime);
    if (!start || !end) return;
    if (end <= start) end = new Date(end.getTime() + 24 * 60 * 60 * 1000);

    const fullRawHours = Math.max(0, (end.getTime() - start.getTime()) / 3600000);
    const fullPaidScheduledHours = Math.max(
      0,
      fullRawHours - Math.max(0, Number(defaultLunchMinutes) || 0) / 60
    );
    totalPublishedScheduledHours += fullPaidScheduledHours;
    if (now <= start) return;
    if (now < end) {
      // Today's scheduled expectation grows only as the scheduled shift elapses.
      const elapsedScheduled = Math.max(0, (now.getTime() - start.getTime()) / 3600000);
      scheduledHoursToDate += elapsedScheduled;
      if (dateKey === todayKey) todayScheduledHours += elapsedScheduled;
      return;
    }

    const completedScheduled = fullPaidScheduledHours;
    scheduledHoursToDate += completedScheduled;
    if (dateKey === todayKey) todayScheduledHours += completedScheduled;
  });

  // Payroll-aware credit: outside-roster time is only credited after a manager
  // approves the schedule variance. Pending/rejected overtime is clipped back
  // to the published roster, while lateness/early departure still reduces credit.
  const monthStart = startOfMonth(now);
  const monthEnd = endOfMonth(now);
  const physicalShifts = dedupePortalShifts(shifts || [], abandonedShiftHours);

  physicalShifts.forEach((shift) => {
    const clockIn = tsToDate(shift?.clockIn);
    if (!clockIn || clockIn < monthStart || clockIn > monthEnd || clockIn > now) return;

    const dateKey = portalDateKey(clockIn);
    const entry = scheduleByDate.get(dateKey);
    const isLive = Boolean(activeShiftId && shift?.id === activeShiftId);
    let actualEnd = isLive ? now : tsToDate(shift?.clockOut || shift?.actualClockOut || shift?.thresholdClockOut);
    if (!actualEnd) {
      const effectiveOutSec = getDisplayClockOutSec(shift, abandonedShiftHours);
      actualEnd = typeof effectiveOutSec === "number" ? new Date(effectiveOutSec * 1000) : null;
    }
    if (!actualEnd || actualEnd <= clockIn) return;

    const approved = shift?.scheduleVarianceApproved === true;
    const isAttendance = entry?.isAttendance === true || Boolean(entry?.attendanceTypeId);
    const isOff = entry?.off === true || String(entry?.publishedShift || "").trim().toUpperCase() === "OFF";

    // Work performed on OFF/leave/unscheduled days only becomes paid credit
    // when the manager explicitly approves the exception.
    if (!entry || isOff || isAttendance) {
      if (!approved) return;
      const raw = Math.max(0, (actualEnd.getTime() - clockIn.getTime()) / 3600000);
      const lunch = isLive ? 0 : getShiftLunchMinutes(shift, defaultLunchMinutes) / 60;
      const credited = Math.max(0, raw - lunch);
      creditedWorkedHoursToDate += credited;
      approvedOvertimeHoursToDate += credited;
      if (dateKey === todayKey) todayCreditedWorkedHours += credited;
      return;
    }

    const { startTime, endTime } = scheduleWindowForPortal(entry);
    let scheduledStart = scheduleDateTimeForPortal(dateKey, startTime);
    let scheduledEnd = scheduleDateTimeForPortal(dateKey, endTime);
    if (!scheduledStart || !scheduledEnd) return;
    if (scheduledEnd <= scheduledStart) scheduledEnd = new Date(scheduledEnd.getTime() + 86400000);

    // Approved exception: preserve the real attendance window. Otherwise clip
    // early arrival / late departure to the roster so pending or rejected OT
    // cannot inflate Staff Portal credited hours.
    const creditStart = approved && clockIn < scheduledStart ? clockIn : new Date(Math.max(clockIn.getTime(), scheduledStart.getTime()));
    const rosterEndToNow = new Date(Math.min(scheduledEnd.getTime(), now.getTime()));
    const creditEnd = approved && actualEnd > scheduledEnd
      ? new Date(Math.min(actualEnd.getTime(), now.getTime()))
      : new Date(Math.min(actualEnd.getTime(), rosterEndToNow.getTime()));

    if (creditEnd <= creditStart) return;
    const raw = (creditEnd.getTime() - creditStart.getTime()) / 3600000;
    const lunch = isLive ? 0 : getShiftLunchMinutes(shift, defaultLunchMinutes) / 60;
    const credited = Math.max(0, raw - lunch);
    creditedWorkedHoursToDate += credited;
    if (dateKey === todayKey) todayCreditedWorkedHours += credited;

    if (approved) {
      const regularStart = new Date(Math.max(clockIn.getTime(), scheduledStart.getTime()));
      const regularEnd = new Date(Math.min(actualEnd.getTime(), scheduledEnd.getTime(), now.getTime()));
      const regularRaw = regularEnd > regularStart ? (regularEnd.getTime() - regularStart.getTime()) / 3600000 : 0;
      const regularCredited = Math.max(0, regularRaw - lunch);
      approvedOvertimeHoursToDate += Math.max(0, credited - regularCredited);
    }
  });

  const creditedHours = creditedWorkedHoursToDate + paidLeaveHoursToDate;
  const varianceHours = creditedHours - scheduledHoursToDate;
  const remainingScheduledHours = Math.max(0, totalPublishedScheduledHours - scheduledHoursToDate);
  const projectedMonthEndHours = creditedHours + remainingScheduledHours;
  return {
    scheduledHoursToDate,
    totalPublishedScheduledHours,
    remainingScheduledHours,
    projectedMonthEndHours,
    actualHours: creditedHours,
    workedHours: creditedWorkedHoursToDate,
    paidLeaveHoursToDate,
    approvedOvertimeHoursToDate,
    regularCreditedHoursToDate: Math.max(0, creditedWorkedHoursToDate - approvedOvertimeHoursToDate),
    todayScheduledHours,
    todayCreditedHours: todayCreditedWorkedHours + todayPaidLeaveHours,
    todayPaidLeaveHours,
    creditedHours,
    varianceHours,
  };
};

const formatVarianceDuration = (hours) => {
  const totalMinutes = Math.round(Math.abs(Number(hours) || 0) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (!h) return `${m}m`;
  if (!m) return `${h}h`;
  return `${h}h ${m}m`;
};

const formatDateTime = (value) => {
  const date = tsToDate(value);
  if (!date) return "—";
  return date.toLocaleString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatRelativeShiftTime = (value, now = new Date()) => {
  const date = tsToDate(value);
  if (!date) return "—";

  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const timeText = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (isSameDay(date, now)) return `Today, ${timeText}`;
  if (isSameDay(date, yesterday)) return `Yesterday, ${timeText}`;

  return date.toLocaleString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function shiftDate(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  d.setHours(0, 0, 0, 0);
  return d;
}

function southAfricaHolidaySet(year) {
  const easter = easterSunday(year);
  const holidays = [
    new Date(year, 0, 1),
    new Date(year, 2, 21),
    shiftDate(easter, -2),
    shiftDate(easter, 1),
    new Date(year, 3, 27),
    new Date(year, 4, 1),
    new Date(year, 5, 16),
    new Date(year, 7, 9),
    new Date(year, 8, 24),
    new Date(year, 11, 16),
    new Date(year, 11, 25),
    new Date(year, 11, 26),
  ];

  const keys = new Set();
  holidays.forEach((holiday) => {
    const normalized = new Date(holiday);
    normalized.setHours(0, 0, 0, 0);
    keys.add(normalized.toDateString());
    if (normalized.getDay() === 0) {
      const observed = new Date(normalized);
      observed.setDate(observed.getDate() + 1);
      keys.add(observed.toDateString());
    }
  });

  return keys;
}

function buildPortalMetrics({
  shifts,
  submissions,
  now,
  abandonedShiftHours = DEFAULT_ABANDONED_SHIFT_HOURS,
  defaultLunchMinutes = DEFAULT_LUNCH_MINUTES,
}) {
  const monthStart = startOfMonth(now);
  const monthEnd = endOfMonth(now);
  const weekStart = startOfWeek(now);
  const holidayKeys = southAfricaHolidaySet(now.getFullYear());

  const sortedShifts = [...(shifts || [])].sort((a, b) => {
    const aMs = tsToDate(a.clockIn)?.getTime?.() || 0;
    const bMs = tsToDate(b.clockIn)?.getTime?.() || 0;
    return bMs - aMs;
  });

  const { latestLiveOpenShift, staleOpenShifts } = getPortalOpenShiftState(
    sortedShifts,
    now,
    abandonedShiftHours
  );

  const latestShiftToday =
    sortedShifts.find((shift) => isSameDay(tsToDate(shift.clockIn), now)) || null;

  const payrollLikeShifts = dedupePayrollRows(
    sortedShifts.filter((shift) => {
      const ciSec = toSec(shift?.clockIn);
      if (typeof ciSec !== "number") return false;
      const clockIn = new Date(ciSec * 1000);
      if (Number.isNaN(clockIn.getTime())) return false;
      if (shift?.archived || shift?.isArchived) return false;

      const effectiveOutSec = getDisplayClockOutSec(shift, abandonedShiftHours);
      return typeof effectiveOutSec === "number";
    }),
    (shift) => getDisplayClockOutSec(shift, abandonedShiftHours)
  );

  const todayClosedShifts = payrollLikeShifts.filter((shift) =>
    isSameDay(tsToDate(shift.clockIn), now)
  );

  const weekClosedShifts = payrollLikeShifts.filter((shift) => {
    const clockIn = tsToDate(shift.clockIn);
    return clockIn && clockIn >= weekStart && clockIn <= now;
  });

  const monthClosedShifts = payrollLikeShifts.filter((shift) => {
    const clockIn = tsToDate(shift.clockIn);
    return clockIn && clockIn >= monthStart && clockIn <= monthEnd;
  });

  const sundayClosedShifts = monthClosedShifts.filter((shift) => {
    const clockIn = tsToDate(shift.clockIn);
    return clockIn && clockIn.getDay() === 0;
  });

  const publicHolidayClosedShifts = monthClosedShifts.filter((shift) => {
    const clockIn = tsToDate(shift.clockIn);
    if (!clockIn || clockIn.getDay() === 0) return false;
    const key = new Date(
      clockIn.getFullYear(),
      clockIn.getMonth(),
      clockIn.getDate()
    ).toDateString();
    return holidayKeys.has(key);
  });

  let monthlyHoursWorked = buildPayrollSummary(monthClosedShifts, defaultLunchMinutes, abandonedShiftHours);
  let weeklyHoursWorked = buildPayrollSummary(weekClosedShifts, defaultLunchMinutes, abandonedShiftHours);
  let todayHoursWorked = buildPayrollSummary(todayClosedShifts, defaultLunchMinutes, abandonedShiftHours);
  let sundayHours = buildPayrollSummary(sundayClosedShifts, defaultLunchMinutes, abandonedShiftHours);
  let publicHolidayHours = buildPayrollSummary(publicHolidayClosedShifts, defaultLunchMinutes, abandonedShiftHours);

  if (latestLiveOpenShift) {
    const liveClockIn = tsToDate(latestLiveOpenShift.clockIn);
    const liveRawHours = getShiftRawHours(latestLiveOpenShift, now);

    if (liveClockIn && liveRawHours > 0) {
      if (isSameDay(liveClockIn, now)) todayHoursWorked += liveRawHours;
      if (liveClockIn >= weekStart && liveClockIn <= now) weeklyHoursWorked += liveRawHours;
      if (liveClockIn >= monthStart && liveClockIn <= monthEnd) monthlyHoursWorked += liveRawHours;
      if (liveClockIn.getDay() === 0 && liveClockIn >= monthStart && liveClockIn <= monthEnd) {
        sundayHours += liveRawHours;
      }
      const holidayKey = new Date(
        liveClockIn.getFullYear(),
        liveClockIn.getMonth(),
        liveClockIn.getDate()
      ).toDateString();
      if (liveClockIn.getDay() !== 0 && holidayKeys.has(holidayKey) && liveClockIn >= monthStart && liveClockIn <= monthEnd) {
        publicHolidayHours += liveRawHours;
      }
    }
  }

  let lastClockInAt = null;
  let lastClockOutAt = null;
  const activity = [];

  sortedShifts.forEach((shift) => {
    const clockIn = tsToDate(shift.clockIn);
    const effectiveOutSec = getDisplayClockOutSec(shift, abandonedShiftHours);
    const clockOut = typeof effectiveOutSec === "number" ? new Date(effectiveOutSec * 1000) : null;
    if (!clockIn) return;

    const isLatestLiveOpenShift = latestLiveOpenShift?.id === shift.id;
    const isStaleOpenShift = !clockOut && !isLatestLiveOpenShift;
    const paidHours = clockOut ? computePaidHours(shift, defaultLunchMinutes, abandonedShiftHours) : 0;

    if (!lastClockInAt || clockIn > lastClockInAt) lastClockInAt = clockIn;
    if (clockOut && (!lastClockOutAt || clockOut > lastClockOutAt)) lastClockOutAt = clockOut;

    activity.push({
      id: `${shift.id || clockIn.getTime()}-in`,
      timestamp: clockIn,
      title: "Clocked In",
      meta: formatRelativeShiftTime(clockIn, now),
      tag: isLatestLiveOpenShift ? "On shift" : "Shift started",
      tone: isLatestLiveOpenShift ? "accent" : "muted",
    });

    if (clockOut) {
      activity.push({
        id: `${shift.id || clockOut.getTime()}-out`,
        timestamp: clockOut,
        title: "Clocked Out",
        meta: formatRelativeShiftTime(clockOut, now),
        tag: `${formatHours(paidHours)} paid hrs`,
        tone: "muted",
      });
    } else if (isStaleOpenShift) {
      activity.push({
        id: `${shift.id || clockIn.getTime()}-stale-open`,
        timestamp: clockIn,
        title: "Previous Shift Missing Clock-Out",
        meta: `Started ${formatRelativeShiftTime(clockIn, now)}`,
        tag: `Older than ${formatHours(abandonedShiftHours)} hrs`,
        tone: "warn",
      });
    }

    if (shift.lunchUpdatedAt || typeof shift.lunchTaken === "boolean") {
      activity.push({
        id: `${shift.id || clockIn.getTime()}-lunch`,
        timestamp: tsToDate(shift.lunchUpdatedAt) || clockIn,
        title: "Lunch Break Status",
        meta: shift.lunchTaken
          ? "Lunch break marked as taken"
          : shift.lunchReason
          ? `No lunch break taken — ${shift.lunchReason}`
          : "No lunch break taken",
        tag: shift.lunchTaken ? "Taken" : "Flagged",
        tone: shift.lunchTaken ? "ok" : "warn",
      });
    }

    const breakStartedAt = tsToDate(shift.breakStartedAt);
    const breakEndedAt = tsToDate(shift.breakEndedAt);
    const lastBreakMinutes = Math.max(0, Math.floor(Number(shift.lastBreakMinutes) || 0));

    if (isLatestLiveOpenShift && shift.breakStatus === "on_break" && breakStartedAt) {
      const currentBreakMinutes = Math.max(0, Math.floor((now.getTime() - breakStartedAt.getTime()) / 60000));
      activity.push({
        id: `${shift.id || clockIn.getTime()}-break-live`,
        timestamp: tsToDate(shift.currentStatusUpdatedAt) || breakStartedAt,
        title: "On Break",
        meta: `Break started ${formatRelativeShiftTime(breakStartedAt, now)}`,
        tag: formatMinutesLabel(currentBreakMinutes),
        tone: "break",
      });
    } else if ((breakEndedAt || shift.currentStatusUpdatedAt) && lastBreakMinutes > 0) {
      activity.push({
        id: `${shift.id || clockIn.getTime()}-break-ended`,
        timestamp: breakEndedAt || tsToDate(shift.currentStatusUpdatedAt) || clockIn,
        title: "Break Taken",
        meta: "Returned to on-shift status",
        tag: formatMinutesLabel(lastBreakMinutes),
        tone: "break",
      });
    }
  });

  let submissionsToday = 0;
  let submissionsWeek = 0;
  let submissionsMonth = 0;

  submissions.forEach((submission) => {
    const submittedAt = tsToDate(submission.submittedAt);
    if (!submittedAt) return;
    if (isSameDay(submittedAt, now)) submissionsToday += 1;
    if (submittedAt >= weekStart) submissionsWeek += 1;
    if (submittedAt >= monthStart && submittedAt <= monthEnd) submissionsMonth += 1;
    activity.push({
      id: submission.id || `${submission.sectionId || submission.sectionName}-${submittedAt.getTime()}`,
      timestamp: submittedAt,
      title: `Submitted ${submission.sectionName || "workspace item"}`,
      meta: formatDateTime(submittedAt),
      tag: "Submitted",
      tone: "ok",
    });
  });

  activity.sort((a, b) => (b.timestamp?.getTime?.() || 0) - (a.timestamp?.getTime?.() || 0));

  const activeShiftForStatus = latestLiveOpenShift || latestShiftToday;
  const activeBreakStartedAt = tsToDate(activeShiftForStatus?.breakStartedAt);
  const currentBreakMinutes = latestLiveOpenShift?.breakStatus === "on_break" && activeBreakStartedAt
    ? Math.max(0, Math.floor((now.getTime() - activeBreakStartedAt.getTime()) / 60000))
    : 0;

  const sortedStaleOpenShifts = [...staleOpenShifts].sort(
    (a, b) => (tsToDate(a.clockIn)?.getTime?.() || 0) - (tsToDate(b.clockIn)?.getTime?.() || 0)
  );

  return {
    monthlyHoursWorkedExact: Math.max(0, Number(monthlyHoursWorked) || 0),
    monthlyHoursWorked: roundHours(monthlyHoursWorked),
    weeklyHoursWorked: roundHours(weeklyHoursWorked),
    todayHoursWorked: roundHours(todayHoursWorked),
    sundayHours: roundHours(sundayHours),
    publicHolidayHours: roundHours(publicHolidayHours),
    submissionsToday,
    submissionsWeek,
    submissionsMonth,
    lastClockInAt,
    lastClockOutAt,
    shiftStatus: latestLiveOpenShift
      ? latestLiveOpenShift.breakStatus === "on_break"
        ? "On Break"
        : "On Shift"
      : "Off Shift",
    openShift: latestLiveOpenShift,
    lunchShift: latestLiveOpenShift || latestShiftToday,
    currentBreakMinutes,
    lastBreakMinutes: Number(activeShiftForStatus?.lastBreakMinutes || 0),
    activity: activity.slice(0, 8),
    staleOpenShiftCount: staleOpenShifts.length,
    hasStaleOpenShift: staleOpenShifts.length > 0,
    staleOpenShiftStartedAt: tsToDate(sortedStaleOpenShifts[0]?.clockIn) || null,
  };
}

export default function StaffPortalPage() {
  const navigate = useNavigate();
  const { themeMode } = useTheme();
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth || 1024);
  const isMobilePortrait = viewportWidth <= 768;
  const styles = useMemo(() => getStyles(themeMode, { isMobilePortrait }), [themeMode, isMobilePortrait]);

  const session = useMemo(() => getStaffSession(), []);
  const orgId = session?.orgId || localStorage.getItem("orgId") || "";
  const storeId = session?.storeId || localStorage.getItem("storeId") || "";
  const staffName =
    session?.name ||
    localStorage.getItem("staffName") ||
    localStorage.getItem("cashup_staff_name") ||
    "Staff Member";
  const storeName =
    session?.storeName ||
    localStorage.getItem("storeName") ||
    localStorage.getItem("storeLabel") ||
    "Your Store";

  const [now, setNow] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [lunchTaken, setLunchTaken] = useState(true);
  const [lunchReason, setLunchReason] = useState("");
  const [breakStatus, setBreakStatus] = useState("on_shift");
  const [lunchSaveState, setLunchSaveState] = useState("idle");
  const [breakSaveState, setBreakSaveState] = useState("idle");
  const [loadingShifts, setLoadingShifts] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const [monthlyTarget, setMonthlyTarget] = useState(DEFAULT_MONTHLY_TARGET);
  const [abandonedShiftHours, setAbandonedShiftHours] = useState(DEFAULT_ABANDONED_SHIFT_HOURS);
  const [defaultLunchMinutes, setDefaultLunchMinutes] = useState(DEFAULT_LUNCH_MINUTES);
  const [legacyShifts, setLegacyShifts] = useState([]);
  const [publishedMonthSchedule, setPublishedMonthSchedule] = useState([]);
  const [scheduleVarianceLoading, setScheduleVarianceLoading] = useState(true);
  const [scheduleVarianceError, setScheduleVarianceError] = useState("");
  const [varianceDetailOpen, setVarianceDetailOpen] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveBalanceSummary, setLeaveBalanceSummary] = useState(null);
  const [leaveBalanceLoading, setLeaveBalanceLoading] = useState(false);
  const [leavePolicies, setLeavePolicies] = useState([]);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveType, setLeaveType] = useState("annual");
  const [leaveStart, setLeaveStart] = useState("");
  const [leaveEnd, setLeaveEnd] = useState("");
  const [leaveReason, setLeaveReason] = useState("");
  const [leaveEvidence, setLeaveEvidence] = useState(null);
  const [leaveSubmitState, setLeaveSubmitState] = useState("idle");
  const [leaveCancelState, setLeaveCancelState] = useState("idle");
  const [leaveError, setLeaveError] = useState("");
  const [selectedLeaveDetail, setSelectedLeaveDetail] = useState(null);
  const [leaveCancelConfirm, setLeaveCancelConfirm] = useState(null);
  const lunchSaveTimerRef = useRef(null);
  const breakSaveTimerRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth || 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!orgId || !storeId) return;
    let mounted = true;
    (async () => {
      try {
        const [cashupStoreSnap, laborSnap, storeSnap] = await Promise.all([
          getDoc(doc(db, "orgs", orgId, "cashup_stores", storeId)),
          getDoc(doc(db, "orgs", orgId, "settings", "labor")),
          getDoc(doc(db, "orgs", orgId, "stores", storeId)),
        ]);
        const cashupStoreData = cashupStoreSnap.exists() ? cashupStoreSnap.data() || {} : {};
        const storeData = storeSnap.exists() ? storeSnap.data() || {} : {};
        const laborData = laborSnap.exists() ? laborSnap.data() || {} : {};
        const target = Number(
          cashupStoreData?.monthlyTargetHours ??
          storeData?.monthlyTargetHours ??
          DEFAULT_MONTHLY_TARGET
        );
        const abandoned = clampLiveShiftHours(
          cashupStoreData?.abandonedShiftHours ??
          storeData?.abandonedShiftHours ??
          laborData?.abandonedShiftHours ??
          DEFAULT_ABANDONED_SHIFT_HOURS
        );
        const lunch = Number(localStorage.getItem("labor_lunchMin") || laborData?.defaultLunchMinutes || DEFAULT_LUNCH_MINUTES);
        if (!mounted) return;
        setMonthlyTarget(target > 0 ? target : DEFAULT_MONTHLY_TARGET);
        setAbandonedShiftHours(abandoned);
        setDefaultLunchMinutes(lunch >= 0 ? lunch : DEFAULT_LUNCH_MINUTES);
        setLeavePolicies(Array.isArray(laborData?.attendanceTypes) ? laborData.attendanceTypes : []);
      } catch {
        if (!mounted) return;
        setMonthlyTarget(DEFAULT_MONTHLY_TARGET);
        setAbandonedShiftHours(DEFAULT_ABANDONED_SHIFT_HOURS);
        setDefaultLunchMinutes(DEFAULT_LUNCH_MINUTES);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [orgId, storeId]);

  useEffect(() => {
    if (!orgId || !storeId || !staffName) {
      setPublishedMonthSchedule([]);
      setScheduleVarianceLoading(false);
      return undefined;
    }

    let cancelled = false;
    setScheduleVarianceLoading(true);
    setScheduleVarianceError("");

    (async () => {
      try {
        const monthKey = portalMonthKey(new Date());

        // Cost rule: Schedule Variance is an on-demand monthly read, not a new
        // realtime listener. Equality filters keep the query scoped to this
        // signed-in staff member, this store and this month.
        const snap = await getDocs(
          query(
            collection(db, "orgs", orgId, "neo_schedule_entries"),
            where("monthKey", "==", monthKey),
            where("storeId", "==", storeId),
            where("staffName", "==", staffName)
          )
        );

        if (cancelled) return;

        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => row.published === true && String(row.draftStatus || "") === "published")
          .filter((row) => String(row.date || "").startsWith(monthKey))
          .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));

        setPublishedMonthSchedule(rows);
      } catch (error) {
        if (cancelled) return;
        console.error("[Neo Staff Portal] Schedule variance load failed", error);
        setPublishedMonthSchedule([]);
        setScheduleVarianceError("Schedule variance is temporarily unavailable.");
      } finally {
        if (!cancelled) setScheduleVarianceLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, storeId, staffName]);

  useEffect(() => {
    if (!orgId || !storeId || !staffName) return undefined;

    // C4 cost optimisation: this portal only renders the signed-in staff
    // member's shifts. Query that person in Firestore instead of downloading
    // the store's complete historical shift collection and filtering locally.
    const staffShiftQuery = query(
      collection(db, "orgs", orgId, "stores", storeId, "shifts"),
      where("name", "==", staffName)
    );

    const unsub = onSnapshot(
      staffShiftQuery,
      (snap) => {
        const normalizedStaffName = String(staffName || "").trim().toLowerCase();
        const nested = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}), __source: "nested" }))
          .filter((item) => !isArchivedRecord(item))
          .filter((item) => String(item.name || "").trim().toLowerCase() === normalizedStaffName);

        const merged = [...nested, ...(legacyShifts || [])];
        setShifts(dedupePortalShifts(merged, abandonedShiftHours));
        setLoadingShifts(false);
      },
      () => setLoadingShifts(false)
    );

    return () => unsub();
  }, [orgId, storeId, staffName, legacyShifts]);

  useEffect(() => {
    if (!orgId || !storeId || !staffName) return;
    let mounted = true;

    (async () => {
      try {
        const normalizedStoreName = String(storeName || "").trim().toLowerCase();
        const legacySnap = await getDocs(
          query(
            collection(db, "cashup_shifts"),
            where("orgId", "==", orgId),
            where("name", "==", staffName)
          )
        );

        if (!mounted) return;

        const legacy = legacySnap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}), __source: "legacy" }))
          .filter((item) => !isArchivedRecord(item))
          .filter((item) => {
            const itemStoreId = String(item.storeId || item.store || "").trim();
            const itemStoreName = String(item.storeName || item.storeLabel || item.store || "").trim().toLowerCase();
            return itemStoreId === String(storeId) || itemStoreName === normalizedStoreName;
          });

        setLegacyShifts(legacy);
        setShifts((current) => {
          const currentNested = (current || []).filter((item) => item?.__source === "nested");
          return dedupePortalShifts([...currentNested, ...legacy], abandonedShiftHours);
        });
      } catch {
        if (mounted) setLegacyShifts([]);
      } finally {
        if (mounted) setLoadingShifts(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [orgId, storeId, staffName, storeName]);

  useEffect(() => {
    if (!orgId || !storeId || !staffName) return undefined;

    const unsub = onSnapshot(
      collection(db, "orgs", orgId, "stores", storeId, "submissions"),
      (snap) => {
        const normalizedName = String(staffName).trim().toLowerCase();
        const list = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((item) => !isArchivedRecord(item))
          .filter((item) => {
            const by = String(item.submittedBy || item.staffName || "")
              .trim()
              .toLowerCase();
            return by === normalizedName;
          })
          .sort((a, b) => {
            const aMs = tsToDate(a.submittedAt)?.getTime?.() || 0;
            const bMs = tsToDate(b.submittedAt)?.getTime?.() || 0;
            return bMs - aMs;
          });
        setSubmissions(list);
        setLoadingSubmissions(false);
      },
      () => setLoadingSubmissions(false)
    );

    return () => unsub();
  }, [orgId, storeId, staffName]);

  useEffect(() => {
    if (!orgId || !storeId || !staffName) return undefined;
    let cancelled = false;

    // C7: leave history is FRESH, not LIVE. The staff member receives decision
    // notifications through the event-driven response pipeline; the portal only
    // needs a narrow one-shot read when opened/focused.
    const loadStaffLeaveRequests = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, "orgs", orgId, "stores", storeId, "leaveRequests"),
          where("staffName", "==", staffName)
        ));
        if (cancelled) return;
        setLeaveRequests(snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }))
          .sort((a,b) => (tsToDate(b.submittedAt)?.getTime?.() || 0) - (tsToDate(a.submittedAt)?.getTime?.() || 0)));
      } catch (error) {
        console.warn("Could not load staff leave requests", error);
      }
    };

    loadStaffLeaveRequests();
    const onFocus = () => loadStaffLeaveRequests();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; window.removeEventListener("focus", onFocus); };
  }, [orgId, storeId, staffName]);

  useEffect(() => {
    if (!orgId || !storeId || !staffName) return undefined;
    let alive = true;

    const loadBalance = async () => {
      setLeaveBalanceLoading(true);
      try {
        // LR11 intentionally uses an on-demand point query, not a realtime listener.
        // Balance summaries change infrequently and the immutable ledger is never loaded here.
        const snap = await getDocs(query(
          collection(db, "orgs", orgId, "stores", storeId, "leaveBalances"),
          where("staffName", "==", staffName)
        ));
        if (!alive) return;
        const row = snap.docs[0];
        setLeaveBalanceSummary(row ? { id: row.id, ...(row.data() || {}) } : null);
      } catch (error) {
        console.warn("Could not load staff leave balance", error);
        if (alive) setLeaveBalanceSummary(null);
      } finally {
        if (alive) setLeaveBalanceLoading(false);
      }
    };

    loadBalance();
    const onFocus = () => loadBalance();
    window.addEventListener("focus", onFocus);
    return () => { alive = false; window.removeEventListener("focus", onFocus); };
  }, [orgId, storeId, staffName]);

  const submitLeaveRequest = async (e) => {
    e.preventDefault();
    setLeaveError("");
    if (!leaveStart || !leaveEnd) return setLeaveError("Choose a start and end date.");
    if (leaveEnd < leaveStart) return setLeaveError("End date cannot be before start date.");
    if (approvedLeaveConflict) return setLeaveError(`These dates overlap approved leave (${approvedLeaveConflict.start}${approvedLeaveConflict.end !== approvedLeaveConflict.start ? ` → ${approvedLeaveConflict.end}` : ""}). Choose different dates.`);
    if (insufficientLeaveBalance) return setLeaveError(`You need ${leaveDaysText(requestedLeaveDays)} but only ${leaveDaysText(selectedLeaveBalance?.available || 0)} is available.`);
    setLeaveSubmitState("saving");
    try {
      let evidence = null;
      if (leaveEvidence) {
        const safeName = String(leaveEvidence.name || "evidence").replace(/[^a-zA-Z0-9._-]/g, "_");
        const path = `leave_evidence/${orgId}/${storeId}/${Date.now()}_${safeName}`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, leaveEvidence, { contentType: leaveEvidence.type || undefined });
        evidence = { url: await getDownloadURL(fileRef), path, name: leaveEvidence.name || safeName, type: leaveEvidence.type || "" };
      }
      const requestPayload = {
        orgId, storeId, storeName, staffName, staffRole: session?.role || localStorage.getItem("staffRole") || "",
        leaveType, startDate: leaveStart, endDate: leaveEnd, requestedDays: requestedLeaveDays, reason: leaveReason.trim(), evidence,
        status: "pending", submittedAt: serverTimestamp(), submittedBy: staffName, source: "staff_portal", schemaVersion: 1,
      };
      const createdRef = await addDoc(collection(db, "orgs", orgId, "stores", storeId, "leaveRequests"), requestPayload);
      // No realtime leave listener in C7: reflect the successful write locally.
      setLeaveRequests((rows) => [{ ...requestPayload, id: createdRef.id, submittedAt: new Date() }, ...(rows || [])]);
      setLeaveType("annual"); setLeaveStart(""); setLeaveEnd(""); setLeaveReason(""); setLeaveEvidence(null);
      setLeaveOpen(false); setLeaveSubmitState("saved");
    } catch (err) {
      const rawMessage = String(err?.message || "");
      const duplicate = /already exists|already-exists|document exists/i.test(rawMessage);
      setLeaveError(
        duplicate
          ? "This leave request has already been submitted and is already in Neo."
          : rawMessage || "Could not submit leave request."
      );
      setLeaveSubmitState("error");
    }
  };

  const refreshLeaveBalanceAfterCancellation = async () => {
    try {
      const snap = await getDocs(query(
        collection(db, "orgs", orgId, "stores", storeId, "leaveBalances"),
        where("staffName", "==", staffName)
      ));
      const row = snap.docs[0];
      setLeaveBalanceSummary(row ? { id: row.id, ...(row.data() || {}) } : null);
    } catch (error) {
      console.warn("Could not refresh leave balance after cancellation", error);
    }
  };

  const refreshPublishedScheduleAfterCancellation = async () => {
    try {
      const monthKey = portalMonthKey(new Date());
      const snap = await getDocs(query(
        collection(db, "orgs", orgId, "neo_schedule_entries"),
        where("monthKey", "==", monthKey),
        where("storeId", "==", storeId),
        where("staffName", "==", staffName)
      ));
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((row) => row.published === true && String(row.draftStatus || "") === "published")
        .filter((row) => String(row.date || "").startsWith(monthKey))
        .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
      setPublishedMonthSchedule(rows);
    } catch (error) {
      console.warn("Could not refresh published schedule after leave cancellation", error);
    }
  };

  const cancelLeaveRequest = (request) => {
    if (!request?.id) return;
    const status = String(request?.status || "pending").toLowerCase();
    if (!["pending", "approved"].includes(status)) return;

    const todayKey = portalDateKey(new Date());
    if (status === "approved" && String(request.startDate || "") <= todayKey) {
      setLeaveError("Approved leave can be cancelled by staff until the day before it starts. Once leave has started, ask a manager to adjust it so worked hours and leave usage stay accurate.");
      return;
    }

    setLeaveError("");
    setLeaveCancelState("idle");
    setLeaveCancelConfirm(request);
  };

  const confirmCancelLeaveRequest = async () => {
    const request = leaveCancelConfirm;
    if (!request?.id) return;
    const status = String(request?.status || "pending").toLowerCase();

    setLeaveCancelState("saving");
    setLeaveError("");
    try {
      if (status === "approved") {
        const cancelApprovedLeave = httpsCallable(functions, "cancelNeoApprovedLeaveRequest");
        await cancelApprovedLeave({ orgId, storeId, requestId: request.id, staffName });
        await Promise.all([
          refreshLeaveBalanceAfterCancellation(),
          refreshPublishedScheduleAfterCancellation(),
        ]);
      } else {
        await updateDoc(doc(db, "orgs", orgId, "stores", storeId, "leaveRequests", request.id), {
          status: "cancelled",
          cancelledAt: serverTimestamp(),
          cancelledBy: staffName,
          cancelledVia: "staff_portal",
        });
      }

      setLeaveRequests((rows) => (rows || []).map((row) => row.id === request.id
        ? {
            ...row,
            status: "cancelled",
            decision: "cancelled",
            cancelledAt: new Date(),
            cancelledBy: staffName,
            cancelledAfterApproval: status === "approved",
          }
        : row));
      setLeaveCancelConfirm(null);
      setSelectedLeaveDetail(null);
      setLeaveCancelState("saved");
    } catch (err) {
      const message = String(err?.message || "Could not cancel leave request.")
        .replace(/^FirebaseError:\s*/i, "")
        .replace(/^functions\/[a-z-]+:\s*/i, "");
      setLeaveError(message);
      setLeaveCancelState("error");
    }
  };

  const visibleLeaveRequests = useMemo(() => {
    const localToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayKey = `${localToday.getFullYear()}-${String(localToday.getMonth() + 1).padStart(2, "0")}-${String(localToday.getDate()).padStart(2, "0")}`;
    return (leaveRequests || [])
      .filter((request) => {
        const status = String(request?.status || "pending").toLowerCase();
        if (status === "cancelled" || status === "canceled") return false;
        if (status === "pending") return true;
        const endKey = String(request?.endDate || request?.startDate || "");
        return !endKey || endKey >= todayKey;
      })
      .slice(0, 6);
  }, [leaveRequests, now]);


  const staffLeaveBalances = useMemo(() => {
    const balances = leaveBalanceSummary?.balances || {};
    const pretty = {
      "annual-leave": "Annual Leave", annual: "Annual Leave",
      "sick-leave": "Sick Leave", sick: "Sick Leave",
      "family-responsibility-leave": "Family Responsibility Leave", family_responsibility: "Family Responsibility Leave",
      "birthday-leave": "Birthday Leave",
      "paid-leave": "Paid Leave",
      "unpaid-leave": "Unpaid Leave", unpaid: "Unpaid Leave",
    };
    return Object.entries(balances).map(([id, raw]) => {
      const opening = Number(raw?.openingBalanceDays || 0);
      const accrued = Number(raw?.accruedDays || 0);
      const adjustments = Number(raw?.manualAdjustmentDays || 0);
      const booked = Math.max(0, Number(raw?.bookedDays || 0));
      const used = Math.max(0, Number(raw?.usedDays || 0));
      // Available leave is derived from entitlement less booked/used leave.
      // Do not trust a legacy stored `availableDays` value because older approval
      // flows could leave it stale when bookedDays changed.
      const available = opening + accrued + adjustments - booked - used;
      return { id, label: raw?.leaveTypeLabel || pretty[id] || String(id).replace(/[-_]/g, " ").replace(/\b\w/g, c => c.toUpperCase()), opening, accrued, adjustments, booked, used, available };
    }).sort((a,b) => a.label.localeCompare(b.label));
  }, [leaveBalanceSummary]);

  const leaveDaysText = (value) => {
    const n = Math.round((Number(value) || 0) * 100) / 100;
    return `${Number.isInteger(n) ? n : n.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")} day${Math.abs(n) === 1 ? "" : "s"}`;
  };

  const requestedLeaveDays = useMemo(() => {
    if (!leaveStart || !leaveEnd || leaveEnd < leaveStart) return 0;
    const start = new Date(`${leaveStart}T12:00:00`);
    const end = new Date(`${leaveEnd}T12:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
    return Math.max(0, Math.floor((end.getTime() - start.getTime()) / 86400000) + 1);
  }, [leaveStart, leaveEnd]);

  const approvedLeaveRanges = useMemo(
    () => (leaveRequests || [])
      .filter((request) => String(request?.status || "").toLowerCase() === "approved")
      .map((request) => ({
        id: request.id,
        start: String(request?.startDate || ""),
        end: String(request?.endDate || request?.startDate || ""),
      }))
      .filter((range) => range.start && range.end),
    [leaveRequests]
  );

  const dateFallsWithinApprovedLeave = (dateKey) =>
    Boolean(dateKey) && approvedLeaveRanges.some((range) => dateKey >= range.start && dateKey <= range.end);

  const approvedLeaveConflict = useMemo(() => {
    if (!leaveStart || !leaveEnd || leaveEnd < leaveStart) return null;
    return approvedLeaveRanges.find((range) => leaveStart <= range.end && leaveEnd >= range.start) || null;
  }, [leaveStart, leaveEnd, approvedLeaveRanges]);

  const selectedLeaveBalance = useMemo(() => {
    const aliases = {
      annual: "annual-leave",
      sick: "sick-leave",
      family_responsibility: "family-responsibility-leave",
      unpaid: "unpaid-leave",
      other: "paid-leave",
    };
    const wanted = aliases[leaveType] || leaveType;
    return staffLeaveBalances.find((row) => row.id === wanted) || null;
  }, [leaveType, staffLeaveBalances]);

  const selectedLeavePolicy = useMemo(() => {
    const aliases = {
      annual: "annual-leave",
      sick: "sick-leave",
      family_responsibility: "family-responsibility-leave",
      unpaid: "unpaid-leave",
      other: "paid-leave",
    };
    const wanted = aliases[leaveType] || leaveType;
    return (leavePolicies || []).find((row) => String(row?.id || "") === String(wanted))?.leavePolicy || {};
  }, [leaveType, leavePolicies]);

  const insufficientLeaveBalance =
    selectedLeavePolicy?.blockIfInsufficient === true &&
    requestedLeaveDays > 0 &&
    selectedLeaveBalance &&
    requestedLeaveDays > Number(selectedLeaveBalance.available || 0);

  const leaveTypeLabel = (value) =>
    ({
      annual: "Annual Leave",
      sick: "Sick Leave",
      family_responsibility: "Family Responsibility Leave",
      unpaid: "Unpaid Leave",
      other: "Other / Manual",
    }[value] || "Leave");

  const metrics = useMemo(
    () =>
      buildPortalMetrics({
        shifts,
        submissions,
        now,
        abandonedShiftHours,
        defaultLunchMinutes,
      }),
    [shifts, submissions, now, abandonedShiftHours, defaultLunchMinutes]
  );

  const scheduleVariance = useMemo(() => {
    return buildScheduleVariance({
      entries: publishedMonthSchedule,
      shifts,
      now,
      defaultLunchMinutes,
      abandonedShiftHours,
      activeShiftId: metrics.openShift?.id || "",
    });
  }, [publishedMonthSchedule, shifts, now, defaultLunchMinutes, abandonedShiftHours, metrics.openShift]);

  useEffect(() => {
    const shift = metrics.openShift || metrics.lunchShift;
    if (shift && typeof shift.lunchTaken === "boolean") {
      setLunchTaken(!!shift.lunchTaken);
      setLunchReason(shift.lunchReason || "");
      return;
    }

    const key = `staffPortalLunch:${orgId}:${storeId}:${staffName}:${new Date().toDateString()}`;
    try {
      const saved = JSON.parse(localStorage.getItem(key) || "null");
      if (saved && typeof saved.lunchTaken === "boolean") {
        setLunchTaken(!!saved.lunchTaken);
        setLunchReason(saved.lunchReason || "");
        return;
      }
    } catch {}

    setLunchTaken(true);
    setLunchReason("");
  }, [metrics.openShift, metrics.lunchShift, orgId, storeId, staffName]);

  useEffect(() => {
    return () => {
      if (lunchSaveTimerRef.current) clearTimeout(lunchSaveTimerRef.current);
      if (breakSaveTimerRef.current) clearTimeout(breakSaveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const shift = metrics.openShift || metrics.lunchShift;
    if (shift?.breakStatus === "on_break") {
      setBreakStatus("on_break");
      return;
    }

    setBreakStatus(metrics.openShift ? "on_shift" : "off_shift");
  }, [metrics.openShift, metrics.lunchShift]);

  const persistLunchState = async (nextTaken, nextReason) => {
    const key = `staffPortalLunch:${orgId}:${storeId}:${staffName}:${new Date().toDateString()}`;
    const payload = {
      lunchTaken: !!nextTaken,
      lunchReason: nextTaken ? "" : nextReason || "",
      updatedAt: Date.now(),
    };

    try {
      localStorage.setItem(key, JSON.stringify(payload));
    } catch {}

    const targetShift = metrics.openShift || metrics.lunchShift;
    if (!orgId || !storeId || !targetShift?.id) {
      setLunchSaveState("saved");
      return;
    }

    setLunchSaveState("saving");
    try {
      await updateDoc(doc(db, "orgs", orgId, "stores", storeId, "shifts", targetShift.id), {
        lunchTaken: !!nextTaken,
        lunchReason: nextTaken ? "" : nextReason || "",
        lunchUpdatedAt: serverTimestamp(),
        lunchUpdatedBy: staffName,
      });
      setLunchSaveState("saved");
    } catch {
      setLunchSaveState("error");
    }
  };

  const queueLunchSave = (nextTaken, nextReason, delay = 350) => {
    if (lunchSaveTimerRef.current) clearTimeout(lunchSaveTimerRef.current);
    lunchSaveTimerRef.current = setTimeout(() => {
      persistLunchState(nextTaken, nextReason);
    }, delay);
  };

  const handleToggleLunch = () => {
    const nextTaken = !lunchTaken;
    const nextReason = nextTaken ? "" : lunchReason;
    setLunchTaken(nextTaken);
    if (nextTaken) setLunchReason("");
    queueLunchSave(nextTaken, nextReason, 0);
  };

  const handleLunchReasonChange = (e) => {
    const nextReason = e.target.value;
    setLunchReason(nextReason);
    setLunchSaveState("saving");
    queueLunchSave(false, nextReason, 500);
  };


  const persistBreakState = async (nextStatus) => {
    const targetShift = metrics.openShift;
    if (!orgId || !storeId || !targetShift?.id) {
      setBreakSaveState("error");
      return;
    }

    setBreakSaveState("saving");
    try {
      const payload = {
        breakStatus: nextStatus,
        currentStatusUpdatedAt: serverTimestamp(),
        currentStatusUpdatedBy: staffName,
      };

      if (nextStatus === "on_break") {
        payload.breakStartedAt = serverTimestamp();
      } else {
        const startedAt = tsToDate(targetShift.breakStartedAt);
        const breakMinutes = startedAt
          ? Math.max(0, Math.floor((Date.now() - startedAt.getTime()) / 60000))
          : 0;

        payload.breakEndedAt = serverTimestamp();
        payload.lastBreakMinutes = breakMinutes;
      }

      await updateDoc(doc(db, "orgs", orgId, "stores", storeId, "shifts", targetShift.id), payload);
      setBreakSaveState("saved");
    } catch {
      setBreakSaveState("error");
    }
  };

  const queueBreakSave = (nextStatus, delay = 0) => {
    if (breakSaveTimerRef.current) clearTimeout(breakSaveTimerRef.current);
    breakSaveTimerRef.current = setTimeout(() => {
      persistBreakState(nextStatus);
    }, delay);
  };

  const handleToggleBreak = () => {
    if (!metrics.openShift) return;
    const nextStatus = breakStatus === "on_break" ? "on_shift" : "on_break";
    setBreakStatus(nextStatus);
    queueBreakSave(nextStatus, 0);
  };

  const handleOpenWorkspace = () => {
    ensureStaffSession({
      type: "staff",
      name: staffName,
      role: session?.role || localStorage.getItem("staffRole") || "",
      orgId,
      storeId,
      storeName,
      shiftDocId: metrics.openShift?.id || session?.shiftDocId || null,
    });
    navigate("/staff-dashboard");
  };

  const hour = now.getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  const timeText = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const dateText = now.toLocaleDateString([], {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const monthlyCreditedHours = scheduleVariance.creditedHours ?? metrics.monthlyHoursWorkedExact;
  const cappedMonthlyHours = Math.min(monthlyCreditedHours, monthlyTarget);
  const overtimeHours = Math.max(0, roundHours(monthlyCreditedHours - monthlyTarget));
  const monthProgress = Math.min(
    100,
    Math.round((cappedMonthlyHours / Math.max(1, monthlyTarget)) * 100)
  );
  const varianceMinutes = Math.round(scheduleVariance.varianceHours * 60);
  const varianceAbsLabel = formatVarianceDuration(scheduleVariance.varianceHours);
  const varianceState = Math.abs(varianceMinutes) <= 5 ? "on_schedule" : varianceMinutes > 0 ? "ahead" : "behind";
  const varianceHeadline =
    varianceState === "on_schedule"
      ? "On schedule"
      : varianceState === "ahead"
      ? `${varianceAbsLabel} ahead`
      : `${varianceAbsLabel} behind`;
  const todayVarianceHours = (scheduleVariance.todayCreditedHours || 0) - (scheduleVariance.todayScheduledHours || 0);
  const todayVarianceMinutes = Math.round(todayVarianceHours * 60);
  const todayVarianceState = Math.abs(todayVarianceMinutes) <= 5 ? "on_schedule" : todayVarianceMinutes > 0 ? "ahead" : "behind";
  const todayVarianceLabel = todayVarianceState === "on_schedule"
    ? "Today: on schedule"
    : `Today: ${formatVarianceDuration(todayVarianceHours)} ${todayVarianceState}`;
  const projectedMonthEndHours = scheduleVariance.projectedMonthEndHours || 0;
  const projectionGapHours = projectedMonthEndHours - monthlyTarget;
  const projectionGapMinutes = Math.round(projectionGapHours * 60);
  const projectionState = Math.abs(projectionGapMinutes) <= 5
    ? "target"
    : projectionGapMinutes > 0
    ? "above"
    : "below";
  const projectionStatusLabel = projectionState === "target"
    ? "On track to hit target"
    : projectionState === "above"
    ? `On track · ${formatVarianceDuration(projectionGapHours)} above target`
    : `${formatVarianceDuration(projectionGapHours)} below target`;

  const lastClockIn = formatRelativeShiftTime(metrics.lastClockInAt, now);
  const lastClockOut = formatRelativeShiftTime(metrics.lastClockOutAt, now);

  const loading = loadingShifts || loadingSubmissions;
  const isOnBreak = metrics.shiftStatus === "On Break";
  const isOnShift = metrics.shiftStatus === "On Shift";
  const heroStatusText = isOnBreak ? "On break" : isOnShift ? "On shift" : "Off shift";
  const heroStatusNote = isOnBreak
    ? `On break for ${formatMinutesLabel(metrics.currentBreakMinutes)}`
    : isOnShift
    ? metrics.lastBreakMinutes > 0
      ? `Last break ${formatMinutesLabel(metrics.lastBreakMinutes)}`
      : "Live and clocked in"
    : "No active shift right now";

  return (
    <div className="page" style={styles.page}>
      <div style={styles.surface}>
        <div style={styles.hero}>
          <div style={styles.heroGlow} />
          <div style={styles.heroBadge}>Neo Staff Dashboard</div>

          <div style={styles.heroTopRow}>
            <div style={styles.heroLeft}>
              <div style={styles.eyebrow}>{greeting},</div>
              <h1 style={styles.title}>{staffName}</h1>
              <div style={styles.subtitle}>{storeName}</div>
            </div>

            <div style={styles.heroRight}>
              <div style={styles.liveClock}>{timeText}</div>
              <div style={styles.liveDate}>{dateText}</div>

              <div
                style={
                  isOnBreak
                    ? styles.heroStatusCardBreak
                    : isOnShift
                    ? styles.heroStatusCard
                    : styles.heroStatusCardMuted
                }
              >
                <div style={styles.heroStatusTop}>
                  <span
                    style={
                      isOnBreak
                        ? styles.heroStatusDotBreak
                        : isOnShift
                        ? styles.heroStatusDot
                        : styles.heroStatusDotMuted
                    }
                  />
                  <span style={styles.heroStatusText}>{heroStatusText}</span>
                </div>
                <div style={styles.heroStatusSub}>{heroStatusNote}</div>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.heroGrid}>
          <div style={styles.progressCard}>
            <div style={styles.progressRingWrap}>
              <div
                style={{
                  ...styles.progressRing,
                  background: `conic-gradient(${styles.progressFillColor} ${monthProgress}%, ${styles.progressTrackColor} ${monthProgress}% 100%)`,
                }}
              >
                <div style={styles.progressRingInner}>
                  <div style={styles.progressPercent}>{monthProgress}%</div>
                  <div style={styles.progressLabel}>of target</div>
                </div>
              </div>
            </div>

            <div style={styles.progressMeta}>
              <div style={styles.progressTitle}>Monthly Progress</div>
              <div style={styles.progressBig}>
                {formatHours(cappedMonthlyHours)} / {formatHours(monthlyTarget)} hrs
              </div>
              <div style={styles.progressSub}>
                {monthlyCreditedHours >= monthlyTarget
                  ? "Target met"
                  : "Paid/credited hours from shifts and approved paid leave this month"}
              </div>
              {overtimeHours > 0 ? (
                <div style={styles.progressOvertime}>Overtime: {formatHours(overtimeHours)} hrs</div>
              ) : null}

              <div style={styles.varianceDivider} />
              <button type="button" style={styles.varianceTitleButton} onClick={() => setVarianceDetailOpen(true)} aria-label="Open schedule variance breakdown">
                <span style={styles.varianceTitle}>Schedule Variance</span><span style={styles.varianceOpenHint}>View breakdown →</span>
              </button>
              {scheduleVarianceLoading ? (
                <div style={styles.variancePending}>Calculating against your published schedule…</div>
              ) : scheduleVarianceError ? (
                <div style={styles.varianceUnavailable}>{scheduleVarianceError}</div>
              ) : publishedMonthSchedule.length === 0 ? (
                <div style={styles.variancePending}>No published schedule found for this month.</div>
              ) : (
                <>
                  <div
                    style={
                      varianceState === "ahead"
                        ? styles.varianceAhead
                        : varianceState === "behind"
                        ? styles.varianceBehind
                        : styles.varianceOnSchedule
                    }
                  >
                    {varianceState === "ahead" ? "↑ " : varianceState === "behind" ? "↓ " : "✓ "}
                    {varianceHeadline}
                  </div>
                  <div style={styles.varianceRows}>
                    <div style={styles.varianceRow}>
                      <span>Scheduled to date</span>
                      <strong>{formatVarianceDuration(scheduleVariance.scheduledHoursToDate)}</strong>
                    </div>
                    <div style={styles.varianceRow}>
                      <span>Credited to date</span>
                      <strong>{formatVarianceDuration(scheduleVariance.creditedHours)}</strong>
                    </div>
                  </div>
                  <div style={styles.todayVarianceMini}>{todayVarianceLabel}</div>
                  <div style={styles.projectionMini}>
                    <span>Projected month end</span>
                    <strong>{formatVarianceDuration(projectedMonthEndHours)}</strong>
                    <em style={{ ...(projectionState === "below" ? styles.projectionBelow : styles.projectionOnTrack), ...styles.projectionMiniStatus }}>{projectionStatusLabel}</em>
                  </div>
                  <div style={styles.varianceNote}>Based on your published schedule through right now.</div>
                </>
              )}
            </div>
          </div>

          {varianceDetailOpen ? (
            <div style={styles.varianceBackdrop} onMouseDown={() => setVarianceDetailOpen(false)}>
              <div style={styles.varianceDetailCard} onMouseDown={(e) => e.stopPropagation()}>
                <div style={styles.varianceDetailHeader}>
                  <div>
                    <div style={styles.varianceDetailEyebrow}>NEO SCHEDULE INTELLIGENCE</div>
                    <div style={styles.varianceDetailTitle}>Schedule Variance</div>
                    <div style={styles.varianceDetailMonth}>{`${now.toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" })} - ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}`}</div>
                  </div>
                  <button type="button" style={styles.varianceDetailClose} onClick={() => setVarianceDetailOpen(false)}>×</button>
                </div>
                <div style={varianceState === "ahead" ? styles.varianceAhead : varianceState === "behind" ? styles.varianceBehind : styles.varianceOnSchedule}>
                  {varianceState === "ahead" ? "↑ " : varianceState === "behind" ? "↓ " : "✓ "}{varianceHeadline}
                </div>
                <div style={styles.varianceDetailGrid}>
                  <div style={styles.varianceDetailSection}>
                    <div style={styles.varianceDetailSectionTitle}>MONTH TO DATE</div>
                    <div style={styles.varianceDetailLine}><span>Scheduled to date</span><strong>{formatVarianceDuration(scheduleVariance.scheduledHoursToDate)}</strong></div>
                    <div style={styles.varianceDetailLine}><span>Regular credited</span><strong>{formatVarianceDuration(scheduleVariance.regularCreditedHoursToDate)}</strong></div>
                    <div style={styles.varianceDetailLine}><span>Approved overtime</span><strong>{formatVarianceDuration(scheduleVariance.approvedOvertimeHoursToDate)}</strong></div>
                    <div style={styles.varianceDetailLine}><span>Paid leave credit</span><strong>{formatVarianceDuration(scheduleVariance.paidLeaveHoursToDate)}</strong></div>
                    <div style={styles.varianceDetailTotal}><span>Credited to date</span><strong>{formatVarianceDuration(scheduleVariance.creditedHours)}</strong></div>
                  </div>
                  <div style={styles.varianceDetailSection}>
                    <div style={styles.varianceDetailSectionTitle}>TODAY · LIVE</div>
                    <div style={styles.varianceDetailLine}><span>Scheduled so far</span><strong>{formatVarianceDuration(scheduleVariance.todayScheduledHours)}</strong></div>
                    <div style={styles.varianceDetailLine}><span>Credited so far</span><strong>{formatVarianceDuration(scheduleVariance.todayCreditedHours)}</strong></div>
                    <div style={styles.varianceDetailTotal}><span>Today</span><strong>{todayVarianceState === "on_schedule" ? "On schedule" : `${formatVarianceDuration(todayVarianceHours)} ${todayVarianceState}`}</strong></div>
                  </div>
                </div>
                <div style={styles.varianceInsight}>
                  {varianceState === "ahead"
                    ? `Your approved credited time currently puts you ${varianceAbsLabel} ahead of your published schedule.`
                    : varianceState === "behind"
                    ? `You are currently ${varianceAbsLabel} behind your published schedule to this point in the month.`
                    : "Your credited time is currently aligned with your published schedule."}
                </div>
                <div style={styles.projectionDetailSection}>
                  <div style={styles.varianceDetailSectionTitle}>SV4 · TARGET PROJECTION</div>
                  <div style={styles.projectionHeadline}>{formatVarianceDuration(projectedMonthEndHours)} projected</div>
                  <div style={projectionState === "below" ? styles.projectionBelow : styles.projectionOnTrack}>{projectionStatusLabel}</div>
                  <div style={styles.projectionDetailGrid}>
                    <div style={styles.varianceDetailLine}><span>Published month schedule</span><strong>{formatVarianceDuration(scheduleVariance.totalPublishedScheduledHours)}</strong></div>
                    <div style={styles.varianceDetailLine}><span>Remaining scheduled</span><strong>{formatVarianceDuration(scheduleVariance.remainingScheduledHours)}</strong></div>
                    <div style={styles.varianceDetailLine}><span>Monthly target</span><strong>{formatVarianceDuration(monthlyTarget)}</strong></div>
                  </div>
                  <div style={styles.projectionFootnote}>Projection assumes you complete the rest of your currently published paid schedule. Future approved overtime, leave changes or roster edits will update this projection.</div>
                </div>
              </div>
            </div>
          ) : null}

          <div style={styles.sideInfoCard}>
            <div style={styles.sideInfoRow}>
              <span style={styles.sideInfoLabel}>Last Clock In</span>
              <span style={styles.sideInfoValue}>{lastClockIn}</span>
            </div>

            <div style={styles.sideInfoRow}>
              <span style={styles.sideInfoLabel}>Last Clock Out</span>
              <span style={styles.sideInfoValue}>{lastClockOut}</span>
            </div>

            <div style={styles.sideInfoRow}>
              <span style={styles.sideInfoLabel}>Shift Status</span>

              <button
                type="button"
                onClick={handleToggleBreak}
                disabled={!metrics.openShift || breakSaveState === "saving"}
                style={
                  !metrics.openShift
                    ? styles.breakDisabledBtn
                    : breakStatus === "on_break"
                    ? styles.breakActiveBtn
                    : styles.breakIdleBtn
                }
              >
                <span
                  style={
                    !metrics.openShift
                      ? styles.breakBtnDotMuted
                      : breakStatus === "on_break"
                      ? styles.breakBtnDotBreak
                      : styles.breakBtnDot
                  }
                />
                <span style={styles.breakBtnTextWrap}>
                  <span style={styles.breakBtnTitle}>
                    {!metrics.openShift
                      ? "No active shift"
                      : breakStatus === "on_break"
                      ? "End break"
                      : "Start break"}
                  </span>
                  <span style={styles.breakBtnSub}>
                    {!metrics.openShift
                      ? "Clock in first to change live status"
                      : breakStatus === "on_break"
                      ? `You are currently on break • ${formatMinutesLabel(metrics.currentBreakMinutes)}`
                      : metrics.lastBreakMinutes > 0
                      ? `Last break taken was ${formatMinutesLabel(metrics.lastBreakMinutes)}`
                      : "Manager will see you as on break"}
                  </span>
                </span>
                <span style={styles.breakBtnArrow}>
                  {!metrics.openShift ? "—" : breakStatus === "on_break" ? "↺" : "→"}
                </span>
              </button>

              <div style={styles.saveNote}>
                {breakSaveState === "saving"
                  ? "Saving live status..."
                  : breakSaveState === "saved"
                  ? breakStatus === "on_break"
                    ? `Manager can now see you are on break • ${formatMinutesLabel(metrics.currentBreakMinutes)}`
                    : metrics.lastBreakMinutes > 0
                    ? `Break taken • ${formatMinutesLabel(metrics.lastBreakMinutes)}`
                    : "Manager can now see you are back on shift"
                  : breakSaveState === "error"
                  ? "Could not save break status"
                  : metrics.openShift
                  ? breakStatus === "on_break"
                    ? `On break for ${formatMinutesLabel(metrics.currentBreakMinutes)}`
                    : metrics.lastBreakMinutes > 0
                    ? `Break taken • ${formatMinutesLabel(metrics.lastBreakMinutes)}`
                    : "Visible live on manager dashboard"
                  : "Clock in first to change live status"}
              </div>
            </div>

            <div style={styles.sideInfoRow}>
              <span style={styles.sideInfoLabel}>Lunch Break</span>

              <button
                type="button"
                onClick={handleToggleLunch}
                style={lunchTaken ? styles.lunchTakenBtn : styles.lunchMissedBtn}
              >
                {lunchTaken ? "1 hour lunch break taken" : "No lunch break taken"}
              </button>

              {!lunchTaken && (
                <textarea
                  value={lunchReason}
                  onChange={handleLunchReasonChange}
                  placeholder="Reason for not taking lunch break..."
                  style={styles.reasonInput}
                />
              )}

              <div style={styles.saveNote}>
                {lunchSaveState === "saving"
                  ? "Saving lunch status..."
                  : lunchSaveState === "saved"
                  ? "Lunch status saved"
                  : lunchSaveState === "error"
                  ? "Could not save lunch status"
                  : metrics.openShift || metrics.lunchShift
                  ? "Linked to latest live shift"
                  : "Will attach once a shift is available"}
              </div>
            </div>
          </div>
        </div>

        {null}

        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Today</div>
            <div style={styles.statValue}>{formatHours(metrics.todayHoursWorked)} hrs</div>
            <div style={styles.statSub}>Worked so far</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statLabel}>This Week</div>
            <div style={styles.statValue}>{formatHours(metrics.weeklyHoursWorked)} hrs</div>
            <div style={styles.statSub}>Current weekly total</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statLabel}>Sunday Hours</div>
            <div style={styles.statValue}>{formatHours(metrics.sundayHours)} hrs</div>
            <div style={styles.statSub}>Sunday shifts this month</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statLabel}>Public Holiday</div>
            <div style={styles.statValue}>{formatHours(metrics.publicHolidayHours)} hrs</div>
            <div style={styles.statSub}>Recognised holiday shifts this year</div>
          </div>
        </div>

        <div style={styles.submissionsGrid}>
          <div style={styles.submissionCard}>
            <div style={styles.statLabel}>Submissions Today</div>
            <div style={styles.statValue}>{metrics.submissionsToday}</div>
            <div style={styles.statSub}>Workspace items submitted today</div>
          </div>

          <div style={styles.submissionCard}>
            <div style={styles.statLabel}>Submissions This Week</div>
            <div style={styles.statValue}>{metrics.submissionsWeek}</div>
            <div style={styles.statSub}>Workspace items submitted this week</div>
          </div>

          <div style={styles.submissionCard}>
            <div style={styles.statLabel}>Submissions This Month</div>
            <div style={styles.statValue}>{metrics.submissionsMonth}</div>
            <div style={styles.statSub}>Workspace items submitted this month</div>
          </div>
        </div>

        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <div><div style={styles.panelTitle}>Leave</div><div style={styles.panelSub}>Request leave and track manager approval.</div></div>
            <button
              style={leaveOpen ? styles.leaveButton : styles.leaveButtonGreen}
              onClick={() => { setLeaveError(""); setLeaveOpen(v => !v); }}
            >{leaveOpen ? "Back" : "Request Leave →"}</button>
          </div>
          <div style={styles.leaveBalanceSection}>
            <div style={styles.leaveBalanceHeading}>
              <div>
                <div style={styles.leaveBalanceEyebrow}>MY LEAVE BALANCE</div>
                <div style={styles.leaveBalanceTitle}>Know what you have before you request</div>
              </div>
              <div style={styles.leaveBalanceAsAt}>Current balance</div>
            </div>
            {leaveBalanceLoading ? (
              <div style={styles.leaveBalanceEmpty}>Loading your leave balance…</div>
            ) : staffLeaveBalances.length === 0 ? (
              <div style={styles.leaveBalanceEmpty}>No leave balance has been allocated to you yet.</div>
            ) : (
              <div style={styles.leaveBalanceGrid}>
                {staffLeaveBalances.map((balance) => (
                  <div key={balance.id} style={styles.leaveBalanceCard}>
                    <div style={styles.leaveBalanceLabel}>{balance.label}</div>
                    <div style={styles.leaveBalanceAvailable}>{leaveDaysText(balance.available)}</div>
                    <div style={styles.leaveBalanceAvailableLabel}>available</div>
                    <div style={styles.leaveBalanceMiniGrid}>
                      <div><span style={styles.leaveBalanceMiniLabel}>Accrued</span><strong>{leaveDaysText(balance.accrued)}</strong></div>
                      <div><span style={styles.leaveBalanceMiniLabel}>Booked</span><strong>{leaveDaysText(balance.booked)}</strong></div>
                      <div><span style={styles.leaveBalanceMiniLabel}>Used</span><strong>{leaveDaysText(balance.used)}</strong></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {leaveOpen && <form onSubmit={submitLeaveRequest} style={styles.leaveForm}>
            <label style={styles.leaveLabel}>Leave type<select value={leaveType} onChange={e=>setLeaveType(e.target.value)} style={styles.leaveInput}>
              <option value="annual" style={{ background: themeMode === "dark" ? "#0b1b31" : "#ffffff", color: themeMode === "dark" ? "#e6eef8" : "#14253a" }}>Annual Leave</option><option value="sick" style={{ background: themeMode === "dark" ? "#0b1b31" : "#ffffff", color: themeMode === "dark" ? "#e6eef8" : "#14253a" }}>Sick Leave</option><option value="family_responsibility" style={{ background: themeMode === "dark" ? "#0b1b31" : "#ffffff", color: themeMode === "dark" ? "#e6eef8" : "#14253a" }}>Family Responsibility Leave</option><option value="unpaid" style={{ background: themeMode === "dark" ? "#0b1b31" : "#ffffff", color: themeMode === "dark" ? "#e6eef8" : "#14253a" }}>Unpaid Leave</option><option value="other" style={{ background: themeMode === "dark" ? "#0b1b31" : "#ffffff", color: themeMode === "dark" ? "#e6eef8" : "#14253a" }}>Other / Manual</option>
            </select></label>
            <div style={styles.leaveDates}>
              <label style={styles.leaveLabel}>From
                <input
                  type="date"
                  value={leaveStart}
                  onChange={e => {
                    const nextStart = e.target.value;
                    if (dateFallsWithinApprovedLeave(nextStart)) {
                      setLeaveError("That date is already covered by approved leave. Choose another date.");
                      return;
                    }
                    setLeaveError("");
                    setLeaveStart(nextStart);
                    if (leaveEnd && nextStart && leaveEnd < nextStart) setLeaveEnd(nextStart);
                  }}
                  style={styles.leaveInput}
                />
              </label>
              <label style={styles.leaveLabel}>To
                <input
                  type="date"
                  value={leaveEnd}
                  min={leaveStart || undefined}
                  onChange={e => {
                    const nextEnd = e.target.value;
                    if (leaveStart && nextEnd && nextEnd < leaveStart) {
                      setLeaveEnd(leaveStart);
                      return;
                    }
                    if (dateFallsWithinApprovedLeave(nextEnd)) {
                      setLeaveError("That date is already covered by approved leave. Choose another date.");
                      return;
                    }
                    setLeaveError("");
                    setLeaveEnd(nextEnd);
                  }}
                  style={styles.leaveInput}
                />
              </label>
            </div>
            <label style={styles.leaveLabel}>Reason / note<textarea value={leaveReason} onChange={e=>setLeaveReason(e.target.value)} placeholder="Optional note for your manager" style={{...styles.leaveInput, minHeight:82, resize:"vertical"}} /></label>
            {requestedLeaveDays > 0 && selectedLeaveBalance && (
              <div style={{ padding: 12, borderRadius: 12, border: `1px solid ${insufficientLeaveBalance ? "#fb7185" : (themeMode === "dark" ? "rgba(148,163,184,.24)" : "rgba(15,23,42,.14)")}`, color: insufficientLeaveBalance ? "#fb7185" : (themeMode === "dark" ? "#9fb0c7" : "#64748b"), fontSize: 12, fontWeight: 800 }}>
                Requesting {leaveDaysText(requestedLeaveDays)} · {leaveDaysText(selectedLeaveBalance.available)} currently available
                {insufficientLeaveBalance ? " · Insufficient balance" : ""}
              </div>
            )}
            {approvedLeaveConflict && (
              <div style={{ padding: 12, borderRadius: 12, border: "1px solid #fb7185", color: "#fb7185", fontSize: 12, fontWeight: 800 }}>
                These dates overlap approved leave: {approvedLeaveConflict.start}
                {approvedLeaveConflict.end !== approvedLeaveConflict.start ? ` → ${approvedLeaveConflict.end}` : ""}. Choose different dates.
              </div>
            )}
            <label style={styles.leaveLabel}>Supporting document / photo{leaveType === "sick" && <span style={styles.leaveHint}> Sick notes can be uploaded or photographed here.</span>}<input type="file" accept="image/*,.pdf" capture="environment" onChange={e=>setLeaveEvidence(e.target.files?.[0] || null)} style={styles.leaveFile}/></label>
            {leaveError && <div style={styles.leaveError}>{leaveError}</div>}
            <button
              type="submit"
              disabled={leaveSubmitState === "saving" || insufficientLeaveBalance || Boolean(approvedLeaveConflict) || (!!leaveStart && !!leaveEnd && leaveEnd < leaveStart)}
              style={{
                ...styles.primaryBtn,
                opacity: (leaveSubmitState === "saving" || insufficientLeaveBalance || approvedLeaveConflict || (!!leaveStart && !!leaveEnd && leaveEnd < leaveStart)) ? 0.5 : 1,
                cursor: (leaveSubmitState === "saving" || insufficientLeaveBalance || approvedLeaveConflict || (!!leaveStart && !!leaveEnd && leaveEnd < leaveStart)) ? "not-allowed" : "pointer",
              }}
            >{leaveSubmitState === "saving" ? "Submitting..." : approvedLeaveConflict ? "Choose different leave dates" : insufficientLeaveBalance ? "Reduce leave days to submit" : "Submit for approval"}</button>
          </form>}
          <div style={styles.leaveList}>
            {visibleLeaveRequests.length === 0 ? (
              <div style={styles.emptyState}>
                No current leave requests. Past leave drops out of this dashboard after its final date.
              </div>
            ) : visibleLeaveRequests.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => { setLeaveError(""); setLeaveCancelState("idle"); setSelectedLeaveDetail(r); }}
                style={{ ...styles.leaveItem, ...styles.leaveItemButton }}
              >
                <div style={{ textAlign: "left" }}>
                  <div style={styles.activityTitle}>{leaveTypeLabel(r.leaveType)}</div>
                  <div style={styles.activityMeta}>
                    {r.startDate}{r.endDate && r.endDate !== r.startDate ? ` → ${r.endDate}` : ""}
                    {r.reason ? " • Reason attached" : ""}
                    {r.evidence?.url ? " • Evidence attached" : ""}
                  </div>
                  <div style={styles.leaveOpenHint}>View request details →</div>
                </div>
                <div style={r.status === "approved" ? styles.leaveApproved : r.status === "rejected" ? styles.leaveRejected : styles.leavePending}>
                  {r.status === "approved" ? "Approved" : r.status === "rejected" ? "Rejected" : "Pending"}
                </div>
              </button>
            ))}
          </div>

          {selectedLeaveDetail && (
            <div style={styles.leaveDetailBackdrop} onClick={() => setSelectedLeaveDetail(null)}>
              <div style={styles.leaveDetailCard} onClick={(e) => e.stopPropagation()}>
                <div style={styles.leaveDetailHeader}>
                  <div>
                    <div style={styles.statLabel}>LEAVE REQUEST</div>
                    <div style={styles.leaveDetailTitle}>{leaveTypeLabel(selectedLeaveDetail.leaveType)}</div>
                    <div style={styles.activityMeta}>
                      {selectedLeaveDetail.startDate}
                      {selectedLeaveDetail.endDate && selectedLeaveDetail.endDate !== selectedLeaveDetail.startDate
                        ? ` → ${selectedLeaveDetail.endDate}`
                        : ""}
                    </div>
                  </div>
                  <button type="button" style={styles.leaveDetailClose} onClick={() => setSelectedLeaveDetail(null)}>×</button>
                </div>

                <div style={styles.leaveDetailGrid}>
                  <div style={styles.leaveDetailSection}>
                    <div style={styles.statLabel}>STATUS</div>
                    <div style={selectedLeaveDetail.status === "approved" ? styles.leaveApproved : selectedLeaveDetail.status === "rejected" ? styles.leaveRejected : styles.leavePending}>
                      {selectedLeaveDetail.status === "approved" ? "Approved" : selectedLeaveDetail.status === "rejected" ? "Rejected" : "Pending"}
                    </div>
                  </div>
                  <div style={styles.leaveDetailSection}>
                    <div style={styles.statLabel}>REASON / NOTE</div>
                    <div style={styles.leaveDetailBody}>{selectedLeaveDetail.reason || "No reason supplied."}</div>
                  </div>
                  {selectedLeaveDetail.managerNote ? (
                    <div style={styles.leaveDetailSection}>
                      <div style={styles.statLabel}>MANAGER NOTE</div>
                      <div style={styles.leaveDetailBody}>{selectedLeaveDetail.managerNote}</div>
                    </div>
                  ) : null}
                  <div style={styles.leaveDetailSection}>
                    <div style={styles.statLabel}>SUPPORTING DOCUMENT</div>
                    {selectedLeaveDetail.evidence?.url ? (
                      <a href={selectedLeaveDetail.evidence.url} target="_blank" rel="noreferrer" style={styles.leaveEvidenceLink}>
                        Open {selectedLeaveDetail.evidence?.name || "supporting document"} →
                      </a>
                    ) : (
                      <div style={styles.leaveDetailBody}>No supporting document attached.</div>
                    )}
                  </div>
                </div>
                {leaveError ? <div style={{ ...styles.leaveError, marginTop: 14 }}>{leaveError}</div> : null}
                {["pending", "approved"].includes(String(selectedLeaveDetail.status || "pending").toLowerCase()) && (
                  <div style={styles.leaveDetailActions}>
                    {String(selectedLeaveDetail.status || "pending").toLowerCase() === "approved" && (
                      <div style={{ ...styles.leaveDetailBody, flex: "1 1 320px", opacity: 0.8 }}>
                        Cancelling before the leave starts restores the previous roster and releases the reserved leave balance automatically.
                      </div>
                    )}
                    <button
                      type="button"
                      disabled={leaveCancelState === "saving"}
                      onClick={() => cancelLeaveRequest(selectedLeaveDetail)}
                      style={styles.leaveCancelBtn}
                    >
                      {leaveCancelState === "saving"
                        ? "Cancelling..."
                        : String(selectedLeaveDetail.status || "pending").toLowerCase() === "approved"
                        ? "Cancel approved leave"
                        : "Cancel leave request"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {leaveCancelConfirm && (
            <div
              style={styles.leaveConfirmBackdrop}
              onClick={() => { if (leaveCancelState !== "saving") setLeaveCancelConfirm(null); }}
            >
              <div style={styles.leaveConfirmCard} onClick={(e) => e.stopPropagation()}>
                <div style={styles.leaveConfirmHeader}>
                  <div style={styles.leaveConfirmIcon}>!</div>
                  <button
                    type="button"
                    aria-label="Close cancellation confirmation"
                    style={styles.leaveDetailClose}
                    disabled={leaveCancelState === "saving"}
                    onClick={() => setLeaveCancelConfirm(null)}
                  >×</button>
                </div>

                <div style={styles.statLabel}>LEAVE CANCELLATION</div>
                <div style={styles.leaveConfirmTitle}>
                  {String(leaveCancelConfirm.status || "pending").toLowerCase() === "approved"
                    ? "Cancel approved leave?"
                    : "Cancel leave request?"}
                </div>
                <div style={styles.leaveConfirmMeta}>
                  {leaveTypeLabel(leaveCancelConfirm.leaveType)} · {leaveCancelConfirm.startDate}
                  {leaveCancelConfirm.endDate && leaveCancelConfirm.endDate !== leaveCancelConfirm.startDate
                    ? ` → ${leaveCancelConfirm.endDate}`
                    : ""}
                </div>

                {String(leaveCancelConfirm.status || "pending").toLowerCase() === "approved" ? (
                  <>
                    <div style={styles.leaveConfirmCopy}>
                      Neo will reverse the approved leave cleanly. Your leave history stays recorded.
                    </div>
                    <div style={styles.leaveConfirmImpactGrid}>
                      <div style={styles.leaveConfirmImpact}>
                        <span style={styles.leaveConfirmImpactLabel}>SCHEDULE</span>
                        <strong>Previous published shifts restored</strong>
                      </div>
                      <div style={styles.leaveConfirmImpact}>
                        <span style={styles.leaveConfirmImpactLabel}>LEAVE BALANCE</span>
                        <strong>Reserved days released</strong>
                      </div>
                      <div style={styles.leaveConfirmImpact}>
                        <span style={styles.leaveConfirmImpactLabel}>LABOR</span>
                        <strong>Future leave rows removed</strong>
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={styles.leaveConfirmCopy}>
                    This request will be withdrawn and will no longer be waiting for manager approval.
                  </div>
                )}

                {leaveError ? <div style={{ ...styles.leaveError, marginTop: 14 }}>{leaveError}</div> : null}

                <div style={styles.leaveConfirmActions}>
                  <button
                    type="button"
                    disabled={leaveCancelState === "saving"}
                    style={styles.leaveConfirmKeepBtn}
                    onClick={() => setLeaveCancelConfirm(null)}
                  >Keep leave</button>
                  <button
                    type="button"
                    disabled={leaveCancelState === "saving"}
                    style={{
                      ...styles.leaveConfirmCancelBtn,
                      opacity: leaveCancelState === "saving" ? 0.65 : 1,
                      cursor: leaveCancelState === "saving" ? "wait" : "pointer",
                    }}
                    onClick={confirmCancelLeaveRequest}
                  >
                    {leaveCancelState === "saving"
                      ? "Cancelling..."
                      : String(leaveCancelConfirm.status || "pending").toLowerCase() === "approved"
                      ? "Yes, cancel approved leave"
                      : "Yes, cancel request"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <div style={styles.panelTitle}>Recent Activity</div>
              <div style={styles.panelSub}>
                {loading ? "Loading your latest work activity..." : "Your latest work activity"}
              </div>
            </div>
          </div>

          <div style={styles.activityList}>
            {!loading && metrics.activity.length === 0 ? (
              <div style={styles.emptyState}>No activity captured for this staff profile yet.</div>
            ) : (
              metrics.activity.map((item) => {
                const dotStyle =
                  item.tone === "warn"
                    ? styles.activityDotSoft
                    : item.tone === "break"
                    ? styles.activityDotBreak
                    : item.tone === "muted"
                    ? styles.activityDotAlt
                    : styles.activityDot;
                const tagStyle =
                  item.tone === "warn"
                    ? styles.activityTagSoft
                    : item.tone === "break"
                    ? styles.activityTagBreak
                    : item.tone === "muted"
                    ? styles.activityTagMuted
                    : styles.activityTag;

                return (
                  <div key={item.id} style={styles.activityItem}>
                    <div style={styles.activityLeft}>
                      <div style={dotStyle} />
                      <div>
                        <div style={styles.activityTitle}>{item.title}</div>
                        <div style={styles.activityMeta}>{item.meta}</div>
                      </div>
                    </div>
                    <div style={tagStyle}>{item.tag}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div style={styles.actionsRow}>
          <button style={styles.primaryBtn} onClick={handleOpenWorkspace}>
            Workspace →
          </button>
          <button style={styles.secondaryBtn} onClick={() => navigate("/")}>
            Entry Portal →
          </button>
        </div>
      </div>
    </div>
  );
}

const getStyles = (themeMode = "dark", options = {}) => {
  const { isMobilePortrait = false } = options;
  const isLight = themeMode === "light";

  const c = {
    pageText: isLight ? "#14253a" : "#e6eef8",
    shellBg: isLight
      ? "radial-gradient(circle at 50% 0%, rgba(42,127,255,0.10), transparent 22%), linear-gradient(180deg, #f3f7fc, #eef3f9)"
      : "radial-gradient(circle at 50% 0%, rgba(77,162,255,0.16), transparent 22%), linear-gradient(180deg, rgba(3,10,22,0.99), rgba(2,8,18,1))",
    shellBorder: isLight ? "rgba(42,127,255,0.10)" : "rgba(98,165,255,0.14)",
    shellShadow: isLight
      ? "0 20px 55px rgba(23,43,77,0.08), inset 0 1px 0 rgba(255,255,255,0.9)"
      : "0 28px 90px rgba(0,0,0,0.52), inset 0 1px 0 rgba(255,255,255,0.04)",

    heroBg: isLight
      ? "linear-gradient(180deg, #ffffff, #f5f9ff)"
      : "linear-gradient(180deg, rgba(9,24,46,0.98), rgba(6,17,34,0.95))",
    heroBorder: isLight ? "rgba(42,127,255,0.10)" : "rgba(97,171,255,0.16)",
    heroShadow: isLight
      ? "0 10px 30px rgba(28,52,84,0.08), inset 0 1px 0 rgba(255,255,255,0.95)"
      : "0 16px 34px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.05)",

    heroBadgeText: isLight ? "#2a7fff" : "#7db8ff",
    heroBadgeBg: isLight ? "rgba(42,127,255,0.08)" : "rgba(57,129,255,0.10)",
    heroBadgeBorder: isLight ? "rgba(42,127,255,0.14)" : "rgba(125,184,255,0.18)",

    title: isLight ? "#10243b" : "#f7fbff",
    subtitle: isLight ? "#60758f" : "#aebfd4",
    accent: isLight ? "#2a7fff" : "#53a7ff",
    timeText: isLight ? "#16324e" : "#f5f9ff",
    dateText: isLight ? "#69809b" : "#a8b9cd",

    shiftBg: isLight ? "rgba(32,183,109,0.10)" : "rgba(61,220,151,0.12)",
    shiftText: isLight ? "#14995a" : "#6ef0b1",
    shiftBorder: isLight ? "rgba(32,183,109,0.14)" : "rgba(61,220,151,0.22)",
    shiftMutedBg: isLight ? "rgba(148,163,184,0.12)" : "rgba(148,163,184,0.12)",
    shiftMutedText: isLight ? "#57708a" : "#bed0e2",
    shiftMutedBorder: isLight ? "rgba(148,163,184,0.16)" : "rgba(148,163,184,0.20)",
    breakBg: isLight ? "rgba(251,191,36,0.14)" : "rgba(245,158,11,0.16)",
    breakText: isLight ? "#c77800" : "#ffbf47",
    breakBorder: isLight ? "rgba(245,158,11,0.18)" : "rgba(245,158,11,0.24)",

    breakBtnIdleBg: isLight
      ? "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(245,249,255,0.98))"
      : "linear-gradient(180deg, rgba(22,42,70,0.98), rgba(14,30,52,0.98))",
    breakBtnIdleBorder: isLight ? "rgba(42,127,255,0.14)" : "rgba(123,186,255,0.22)",
    breakBtnIdleText: isLight ? "#16324e" : "#eef5ff",
    breakBtnIdleSub: isLight ? "#68809c" : "#9eb6d0",

    breakBtnActiveBg: isLight
      ? "linear-gradient(180deg, rgba(255,248,235,0.98), rgba(255,243,214,0.98))"
      : "linear-gradient(180deg, rgba(58,38,8,0.98), rgba(44,28,6,0.98))",
    breakBtnActiveBorder: isLight ? "rgba(245,158,11,0.24)" : "rgba(245,158,11,0.28)",
    breakBtnActiveText: isLight ? "#b86a00" : "#ffcf6b",
    breakBtnActiveSub: isLight ? "#c77800" : "#ffd98c",

    breakBtnDisabledBg: isLight
      ? "linear-gradient(180deg, #f8fbff, #f2f6fb)"
      : "linear-gradient(180deg, rgba(20,33,52,0.94), rgba(13,24,40,0.94))",
    breakBtnDisabledBorder: isLight ? "rgba(148,163,184,0.16)" : "rgba(156,178,205,0.20)",
    breakBtnDisabledText: isLight ? "#6f849c" : "#9bb0c8",

    cardBg: isLight
      ? "linear-gradient(180deg, #ffffff, #f8fbff)"
      : "linear-gradient(180deg, rgba(15,32,58,0.98), rgba(10,24,43,0.98))",
    cardBorder: isLight ? "rgba(42,127,255,0.10)" : "rgba(123,186,255,0.22)",
    cardShadow: isLight
      ? "0 10px 28px rgba(22,47,79,0.08), inset 0 1px 0 rgba(255,255,255,0.95)"
      : "0 18px 34px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.05), 0 0 0 1px rgba(91,160,255,0.05)",

    textMain: isLight ? "#16324e" : "#eef5ff",
    textSoft: isLight ? "#68809c" : "#9eb6d0",
    textMuted: isLight ? "#8195ab" : "#8ea3bc",

    activityTagBg: isLight ? "rgba(32,183,109,0.10)" : "rgba(61,220,151,0.12)",
    activityTagText: isLight ? "#14995a" : "#6ef0b1",

    activityTagSoftBg: isLight ? "rgba(255,107,107,0.10)" : "rgba(255,107,107,0.12)",
    activityTagSoftText: isLight ? "#d64545" : "#ff9a9a",
    activityTagBreakBg: isLight ? "rgba(251,191,36,0.14)" : "rgba(245,158,11,0.16)",
    activityTagBreakText: isLight ? "#c77800" : "#ffbf47",

    activityTagMutedBg: isLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.06)",
    activityTagMutedText: isLight ? "#4f647d" : "#c2d2e5",

    primaryBtnBg: isLight
      ? "linear-gradient(180deg,#2a7fff,#1f63d9)"
      : "linear-gradient(180deg,#2d8cff,#1f6fe0)",
    primaryBtnShadow: isLight
      ? "0 10px 20px rgba(31,99,217,0.18)"
      : "0 12px 24px rgba(47,128,237,0.24)",

    secondaryBtnBg: isLight
      ? "linear-gradient(180deg,#ffffff,#eef5ff)"
      : "linear-gradient(180deg, rgba(20,39,65,0.96), rgba(11,24,44,0.96))",
    secondaryBtnText: isLight ? "#204a75" : "#dce9fb",
    secondaryBtnBorder: isLight ? "rgba(42,127,255,0.12)" : "rgba(111,168,255,0.18)",
    secondaryBtnShadow: isLight
      ? "0 8px 16px rgba(22,47,79,0.06)"
      : "0 10px 20px rgba(0,0,0,0.18)",

    lunchTakenBg: isLight ? "rgba(32,183,109,0.10)" : "rgba(61,220,151,0.12)",
    lunchTakenText: isLight ? "#14995a" : "#6ef0b1",
    lunchTakenBorder: isLight ? "rgba(32,183,109,0.14)" : "rgba(61,220,151,0.20)",

    lunchMissedBg: isLight ? "rgba(255,107,107,0.10)" : "rgba(255,107,107,0.12)",
    lunchMissedText: isLight ? "#d64545" : "#ff9a9a",
    lunchMissedBorder: isLight ? "rgba(255,107,107,0.14)" : "rgba(255,107,107,0.20)",

    reasonBg: isLight ? "#ffffff" : "rgba(17,34,58,0.98)",
    reasonBorder: isLight ? "rgba(42,127,255,0.10)" : "rgba(123,186,255,0.18)",
    reasonText: isLight ? "#16324e" : "#eef5ff",
  };

  const styles = {
    progressFillColor: isLight ? "#2a7fff" : "#53a7ff",
    progressTrackColor: isLight ? "rgba(42,127,255,0.12)" : "rgba(125,184,255,0.12)",

    page: {
      position: "relative",
      padding: 0,
      maxWidth: "100%",
      margin: 0,
      fontFamily: "Inter, Arial, sans-serif",
      color: c.pageText,
      background: "transparent",
    },

    surface: {
      width: "100%",
      borderRadius: 30,
      padding: "14px 10px 22px",
      background: c.shellBg,
      border: `1px solid ${c.shellBorder}`,
      boxShadow: c.shellShadow,
      overflow: "hidden",
      position: "relative",
    },

    hero: {
      position: "relative",
      padding: "22px 20px 20px",
      marginBottom: 16,
      borderRadius: 24,
      background: c.heroBg,
      border: `1px solid ${c.heroBorder}`,
      boxShadow: c.heroShadow,
      overflow: "hidden",
    },

    heroGlow: {
      position: "absolute",
      left: "50%",
      bottom: -28,
      transform: "translateX(-50%)",
      width: 320,
      height: 130,
      background: isLight
        ? "radial-gradient(circle, rgba(42,127,255,0.12), transparent 68%)"
        : "radial-gradient(circle, rgba(52,145,255,0.28), transparent 68%)",
      filter: "blur(12px)",
      pointerEvents: "none",
    },

    heroBadge: {
      position: "relative",
      zIndex: 1,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "7px 14px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: c.heroBadgeText,
      background: c.heroBadgeBg,
      border: `1px solid ${c.heroBadgeBorder}`,
      marginBottom: 18,
      width: "fit-content",
    },

    heroTopRow: {
      position: "relative",
      zIndex: 1,
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      gap: 18,
      alignItems: "center",
    },

    heroLeft: {
      minWidth: 0,
    },

    heroRight: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 8,
      minWidth: 140,
    },

    eyebrow: {
      fontSize: 18,
      color: c.textSoft,
      marginBottom: 6,
      fontWeight: 500,
    },

    title: {
      margin: 0,
      fontSize: "clamp(2rem, 5vw, 3.35rem)",
      lineHeight: 1.04,
      fontWeight: 900,
      letterSpacing: "-0.045em",
      color: c.title,
    },

    subtitle: {
      marginTop: 8,
      fontSize: 15,
      color: c.subtitle,
      maxWidth: 620,
    },

    liveClock: {
      fontSize: "clamp(1.8rem, 4vw, 2.7rem)",
      fontWeight: 900,
      color: c.timeText,
      letterSpacing: "-0.03em",
      lineHeight: 1,
    },

    liveDate: {
      fontSize: 14,
      color: c.dateText,
      textAlign: "right",
    },

    statusPill: {
      background: c.shiftBg,
      color: c.shiftText,
      padding: "6px 12px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      border: `1px solid ${c.shiftBorder}`,
    },

    statusPillMuted: {
      background: c.shiftMutedBg,
      color: c.shiftMutedText,
      padding: "6px 12px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 800,
      border: `1px solid ${c.shiftMutedBorder}`,
    },

    heroStatusCard: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 4,
      padding: "10px 14px",
      borderRadius: 16,
      background: c.shiftBg,
      border: `1px solid ${c.shiftBorder}`,
      minWidth: 180,
    },

    heroStatusCardBreak: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 4,
      padding: "10px 14px",
      borderRadius: 16,
      background: c.breakBg,
      border: `1px solid ${c.breakBorder}`,
      minWidth: 180,
    },

    heroStatusCardMuted: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 4,
      padding: "10px 14px",
      borderRadius: 16,
      background: c.shiftMutedBg,
      border: `1px solid ${c.shiftMutedBorder}`,
      minWidth: 180,
    },

    heroStatusTop: {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },

    heroStatusDot: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: c.shiftText,
      boxShadow: `0 0 0 4px ${c.shiftBg}`,
      flexShrink: 0,
    },

    heroStatusDotBreak: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: c.breakText,
      boxShadow: `0 0 0 4px ${c.breakBg}`,
      flexShrink: 0,
    },

    heroStatusDotMuted: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: c.shiftMutedText,
      boxShadow: `0 0 0 4px ${c.shiftMutedBg}`,
      flexShrink: 0,
    },

    heroStatusText: {
      fontSize: 13,
      fontWeight: 900,
      letterSpacing: "0.02em",
      textTransform: "uppercase",
      color: c.textMain,
    },

    heroStatusSub: {
      fontSize: 12,
      color: c.textSoft,
      textAlign: "right",
    },

    heroGrid: {
      display: "grid",
      gridTemplateColumns: isMobilePortrait ? "1fr" : "1.5fr 1fr",
      gap: 14,
      marginBottom: 14,
    },

    progressCard: {
      borderRadius: 24,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      boxShadow: c.cardShadow,
      padding: "18px 16px",
      display: "grid",
      gridTemplateColumns: isMobilePortrait ? "1fr" : "180px minmax(0,1fr)",
      gap: isMobilePortrait ? 18 : 10,
      alignItems: "center",
      minHeight: 170,
    },

    progressRingWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },

    progressRing: {
      width: 156,
      height: 156,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },

    progressRingInner: {
      width: 114,
      height: 114,
      borderRadius: "50%",
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
    },

    progressPercent: {
      fontSize: 26,
      fontWeight: 900,
      color: c.accent,
      lineHeight: 1,
    },

    progressLabel: {
      marginTop: 8,
      fontSize: 12,
      color: c.textSoft,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      fontWeight: 700,
    },

    progressMeta: {
      minWidth: 0,
      textAlign: isMobilePortrait ? "center" : "left",
    },

    progressTitle: {
      fontSize: 14,
      color: c.textSoft,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      fontWeight: 800,
    },

    progressBig: {
      marginTop: 10,
      fontSize: "clamp(1.8rem, 3vw, 2.45rem)",
      lineHeight: 1.05,
      fontWeight: 900,
      color: c.textMain,
      letterSpacing: "-0.03em",
    },

    progressSub: {
      marginTop: 10,
      fontSize: 14,
      color: c.textSoft,
    },

    progressOvertime: {
      marginTop: 10,
      fontSize: 14,
      fontWeight: 800,
      color: c.breakText,
    },

    varianceDivider: {
      height: 1,
      background: c.cardBorder,
      margin: "16px 0 14px",
    },

    varianceTitleButton: { width: "100%", padding: 0, border: "none", background: "transparent", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, cursor: "pointer", textAlign: "left" },
    varianceOpenHint: { color: c.accent, fontSize: 11, fontWeight: 900, whiteSpace: "nowrap" },
    todayVarianceMini: { marginTop: 7, color: c.textSoft, fontSize: 12, fontWeight: 800 },
    projectionMini: { marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${c.cardBorder}`, display: "grid", gridTemplateColumns: "1fr auto", gap: "3px 12px", alignItems: "center", color: c.textSoft, fontSize: 12 },
    projectionMiniStatus: { gridColumn: "1 / -1" },
    projectionOnTrack: { color: c.okText || "#5ee6ad", fontSize: 12, fontWeight: 900, fontStyle: "normal" },
    projectionBelow: { color: c.warnText || "#fbbf24", fontSize: 12, fontWeight: 900, fontStyle: "normal" },
    varianceBackdrop: { position: "fixed", inset: 0, zIndex: 1450, background: "rgba(2,8,18,.74)", display: "flex", alignItems: isMobilePortrait ? "flex-end" : "center", justifyContent: "center", padding: isMobilePortrait ? 0 : 24 },
    varianceDetailCard: { width: isMobilePortrait ? "100%" : "min(620px, 92vw)", maxHeight: isMobilePortrait ? "88vh" : "84vh", overflowY: "auto", boxSizing: "border-box", padding: isMobilePortrait ? 20 : 26, borderRadius: isMobilePortrait ? "22px 22px 0 0" : 22, border: `1px solid ${c.cardBorder}`, background: c.cardBg, color: c.pageText, boxShadow: c.cardShadow },
    varianceDetailHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 18, marginBottom: 18 },
    varianceDetailEyebrow: { color: c.accent, fontSize: 11, fontWeight: 950, letterSpacing: 1.1 },
    varianceDetailTitle: { marginTop: 6, color: c.title, fontSize: isMobilePortrait ? 25 : 30, fontWeight: 950 },
    varianceDetailMonth: { marginTop: 3, color: c.textSoft, fontSize: 13, fontWeight: 700 },
    varianceDetailClose: { width: 42, height: 42, borderRadius: 14, border: `1px solid ${c.cardBorder}`, background: "transparent", color: c.pageText, fontSize: 26, cursor: "pointer" },
    varianceDetailGrid: { display: "grid", gridTemplateColumns: isMobilePortrait ? "1fr" : "1fr 1fr", gap: 12, marginTop: 18 },
    varianceDetailSection: { padding: 16, borderRadius: 16, border: `1px solid ${c.cardBorder}`, background: isLight ? "rgba(248,251,255,.92)" : "rgba(7,20,38,.72)" },
    varianceDetailSectionTitle: { color: c.accent, fontSize: 10, fontWeight: 950, letterSpacing: 1, marginBottom: 10 },
    varianceDetailLine: { display: "flex", justifyContent: "space-between", gap: 14, padding: "8px 0", color: c.textSoft, fontSize: 13 },
    varianceDetailTotal: { display: "flex", justifyContent: "space-between", gap: 14, marginTop: 6, paddingTop: 12, borderTop: `1px solid ${c.cardBorder}`, color: c.title, fontSize: 14, fontWeight: 900 },
    projectionDetailSection: { marginTop: 12, padding: 16, borderRadius: 16, border: `1px solid ${c.cardBorder}`, background: isLight ? "rgba(248,251,255,.92)" : "rgba(7,20,38,.72)" },
    projectionHeadline: { color: c.title, fontSize: isMobilePortrait ? 21 : 24, fontWeight: 950, marginBottom: 5 },
    projectionDetailGrid: { marginTop: 10, display: "grid", gridTemplateColumns: isMobilePortrait ? "1fr" : "repeat(3, 1fr)", gap: isMobilePortrait ? 0 : 12 },
    projectionFootnote: { marginTop: 10, color: c.textSoft, fontSize: 11, lineHeight: 1.5 },
    varianceInsight: { marginTop: 14, padding: 14, borderRadius: 14, border: `1px dashed ${c.cardBorder}`, color: c.pageText, lineHeight: 1.5, fontSize: 13, fontWeight: 700 },
    varianceTitle: {
      fontSize: 12,
      color: c.textSoft,
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      fontWeight: 900,
    },

    varianceAhead: {
      marginTop: 8,
      fontSize: 19,
      lineHeight: 1.2,
      fontWeight: 900,
      color: c.okText || "#5ee6ad",
    },

    varianceBehind: {
      marginTop: 8,
      fontSize: 19,
      lineHeight: 1.2,
      fontWeight: 900,
      color: c.warnText || "#fbbf24",
    },

    varianceOnSchedule: {
      marginTop: 8,
      fontSize: 19,
      lineHeight: 1.2,
      fontWeight: 900,
      color: c.accent,
    },

    varianceRows: {
      marginTop: 10,
      display: "grid",
      gap: 5,
      width: "100%",
    },

    varianceRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      fontSize: 13,
      color: c.textSoft,
    },

    varianceNote: {
      marginTop: 9,
      fontSize: 12,
      color: c.textSoft,
    },

    variancePending: {
      marginTop: 8,
      fontSize: 13,
      color: c.textSoft,
    },

    varianceUnavailable: {
      marginTop: 8,
      fontSize: 13,
      fontWeight: 700,
      color: c.warnText || "#fbbf24",
    },

    sideInfoCard: {
      borderRadius: 24,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      boxShadow: c.cardShadow,
      padding: "14px 16px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: 14,
      minHeight: 170,
    },

    sideInfoRow: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      paddingBottom: 10,
      borderBottom: `1px solid ${c.cardBorder}`,
    },

    sideInfoLabel: {
      fontSize: 13,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: c.textSoft,
    },

    sideInfoValue: {
      fontSize: 17,
      fontWeight: 800,
      color: c.textMain,
      letterSpacing: "-0.02em",
    },

    breakIdleBtn: {
      width: "100%",
      minHeight: 60,
      padding: "12px 14px",
      borderRadius: 16,
      border: `1px solid ${c.breakBtnIdleBorder}`,
      background: c.breakBtnIdleBg,
      color: c.breakBtnIdleText,
      display: "flex",
      alignItems: "center",
      gap: 12,
      cursor: "pointer",
      boxSizing: "border-box",
      textAlign: "left",
    },

    breakActiveBtn: {
      width: "100%",
      minHeight: 60,
      padding: "12px 14px",
      borderRadius: 16,
      border: `1px solid ${c.breakBtnActiveBorder}`,
      background: c.breakBtnActiveBg,
      color: c.breakBtnActiveText,
      display: "flex",
      alignItems: "center",
      gap: 12,
      cursor: "pointer",
      boxSizing: "border-box",
      textAlign: "left",
    },

    breakDisabledBtn: {
      width: "100%",
      minHeight: 60,
      padding: "12px 14px",
      borderRadius: 16,
      border: `1px solid ${c.breakBtnDisabledBorder}`,
      background: c.breakBtnDisabledBg,
      color: c.breakBtnDisabledText,
      display: "flex",
      alignItems: "center",
      gap: 12,
      cursor: "not-allowed",
      boxSizing: "border-box",
      textAlign: "left",
      opacity: 0.92,
    },

    breakBtnDot: {
      width: 12,
      height: 12,
      borderRadius: "50%",
      background: c.shiftText,
      flexShrink: 0,
    },

    breakBtnDotBreak: {
      width: 12,
      height: 12,
      borderRadius: "50%",
      background: c.breakText,
      flexShrink: 0,
    },

    breakBtnDotMuted: {
      width: 12,
      height: 12,
      borderRadius: "50%",
      background: c.shiftMutedText,
      flexShrink: 0,
    },

    breakBtnTextWrap: {
      display: "flex",
      flexDirection: "column",
      gap: 3,
      minWidth: 0,
      flex: 1,
    },

    breakBtnTitle: {
      fontSize: 15,
      fontWeight: 900,
      color: "inherit",
      lineHeight: 1.1,
    },

    breakBtnSub: {
      fontSize: 12,
      color: c.textSoft,
      lineHeight: 1.25,
    },

    breakBtnArrow: {
      fontSize: 18,
      fontWeight: 900,
      color: "inherit",
      flexShrink: 0,
    },

    lunchTakenBtn: {
      width: "fit-content",
      minHeight: 36,
      padding: "8px 14px",
      borderRadius: 14,
      border: `1px solid ${c.lunchTakenBorder}`,
      background: c.lunchTakenBg,
      color: c.lunchTakenText,
      fontSize: 14,
      fontWeight: 800,
      cursor: "pointer",
    },

    lunchMissedBtn: {
      width: "fit-content",
      minHeight: 36,
      padding: "8px 14px",
      borderRadius: 14,
      border: `1px solid ${c.lunchMissedBorder}`,
      background: c.lunchMissedBg,
      color: c.lunchMissedText,
      fontSize: 14,
      fontWeight: 800,
      cursor: "pointer",
    },

    reasonInput: {
      width: "100%",
      minHeight: 66,
      resize: "vertical",
      borderRadius: 14,
      border: `1px solid ${c.reasonBorder}`,
      background: c.reasonBg,
      color: c.reasonText,
      padding: "12px 14px",
      fontSize: 14,
      outline: "none",
      fontFamily: "inherit",
      boxSizing: "border-box",
    },

    saveNote: {
      fontSize: 12,
      color: c.textMuted,
      marginTop: -2,
    },

    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0,1fr))",
      gap: 12,
      marginBottom: 12,
    },

    statCard: {
      borderRadius: 22,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      boxShadow: c.cardShadow,
      padding: "16px 16px 14px",
      minHeight: 110,
    },

    statLabel: {
      fontSize: 13,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: c.textSoft,
    },

    statValue: {
      marginTop: 12,
      fontSize: "clamp(1.8rem, 3vw, 2.35rem)",
      lineHeight: 1,
      fontWeight: 900,
      color: c.textMain,
      letterSpacing: "-0.035em",
    },

    statSub: {
      marginTop: 10,
      fontSize: 14,
      color: c.textSoft,
    },

    warningCard: {
      marginTop: 16,
      marginBottom: 8,
      borderRadius: 18,
      padding: "14px 16px",
      border: isLight ? "1px solid rgba(255,170,0,0.28)" : "1px solid rgba(255,184,77,0.20)",
      background: isLight ? "rgba(255,196,61,0.10)" : "rgba(255,184,77,0.10)",
      boxShadow: isLight ? "inset 0 1px 0 rgba(255,255,255,0.65)" : "inset 0 1px 0 rgba(255,255,255,0.03)",
    },
    warningTitle: {
      fontSize: 15,
      fontWeight: 800,
      color: isLight ? "#9a5a00" : "#ffd17a",
      marginBottom: 6,
    },
    warningText: {
      fontSize: 13,
      lineHeight: 1.5,
      color: isLight ? "#6d4a11" : "#f7d8a1",
    },

    submissionsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0,1fr))",
      gap: 12,
      marginBottom: 14,
    },

    submissionCard: {
      borderRadius: 22,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      boxShadow: c.cardShadow,
      padding: "16px 16px 14px",
      minHeight: 110,
    },

    panel: {
      borderRadius: 24,
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      boxShadow: c.cardShadow,
      marginBottom: 14,
      overflow: "hidden",
    },

    panelHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "16px 18px 14px",
      borderBottom: `1px solid ${c.cardBorder}`,
    },

    panelTitle: {
      fontSize: 18,
      fontWeight: 900,
      color: c.textMain,
      letterSpacing: "-0.02em",
    },

    panelSub: {
      marginTop: 4,
      fontSize: 13,
      color: c.textSoft,
    },

    activityList: {
      display: "grid",
    },

    emptyState: {
      padding: "18px",
      color: c.textSoft,
      fontSize: 14,
    },

    activityItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 14,
      padding: "16px 18px",
      borderBottom: `1px solid ${c.cardBorder}`,
    },

    activityLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      minWidth: 0,
    },

    activityDot: {
      width: 12,
      height: 12,
      borderRadius: "50%",
      background: c.accent,
      flexShrink: 0,
    },

    activityDotAlt: {
      width: 12,
      height: 12,
      borderRadius: "50%",
      background: "#94a3b8",
      flexShrink: 0,
    },

    activityDotSoft: {
      width: 12,
      height: 12,
      borderRadius: "50%",
      background: "#ff9a9a",
      flexShrink: 0,
    },

    activityDotBreak: {
      width: 12,
      height: 12,
      borderRadius: "50%",
      background: c.breakText,
      flexShrink: 0,
    },

    activityTitle: {
      fontSize: 16,
      fontWeight: 800,
      color: c.textMain,
    },

    activityMeta: {
      marginTop: 4,
      fontSize: 13,
      color: c.textSoft,
    },

    activityTag: {
      background: c.activityTagBg,
      color: c.activityTagText,
      borderRadius: 999,
      padding: "8px 12px",
      fontWeight: 800,
      fontSize: 12,
      whiteSpace: "nowrap",
    },

    activityTagSoft: {
      background: c.activityTagSoftBg,
      color: c.activityTagSoftText,
      borderRadius: 999,
      padding: "8px 12px",
      fontWeight: 800,
      fontSize: 12,
      whiteSpace: "nowrap",
    },

    activityTagMuted: {
      background: c.activityTagMutedBg,
      color: c.activityTagMutedText,
      borderRadius: 999,
      padding: "8px 12px",
      fontWeight: 800,
      fontSize: 12,
      whiteSpace: "nowrap",
    },

    activityTagBreak: {
      background: c.activityTagBreakBg,
      color: c.activityTagBreakText,
      borderRadius: 999,
      padding: "8px 12px",
      fontWeight: 800,
      fontSize: 12,
      whiteSpace: "nowrap",
    },

    leaveBalanceSection: { padding: isMobilePortrait ? 14 : 18, borderBottom: `1px solid ${c.cardBorder}` },
    leaveBalanceHeading: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 14 },
    leaveBalanceEyebrow: { color: c.accent, fontSize: 11, fontWeight: 900, letterSpacing: 1.1 },
    leaveBalanceTitle: { marginTop: 4, color: c.title, fontSize: isMobilePortrait ? 16 : 18, fontWeight: 900 },
    leaveBalanceAsAt: { color: c.textSoft, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" },
    leaveBalanceGrid: { display: "grid", gridTemplateColumns: isMobilePortrait ? "1fr" : "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 },
    leaveBalanceCard: { padding: 14, borderRadius: 16, border: `1px solid ${c.cardBorder}`, background: isLight ? "rgba(248,251,255,.92)" : "rgba(7,20,38,.72)" },
    leaveBalanceLabel: { color: c.textSoft, fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: .5 },
    leaveBalanceAvailable: { marginTop: 5, color: c.title, fontSize: 24, fontWeight: 950 },
    leaveBalanceAvailableLabel: { color: c.textSoft, fontSize: 11, fontWeight: 700 },
    leaveBalanceMiniGrid: { marginTop: 12, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, color: c.pageText, fontSize: 11 },
    leaveBalanceMiniLabel: { display: "block", color: c.textSoft, fontSize: 9, fontWeight: 900, textTransform: "uppercase", marginBottom: 3 },
    leaveBalanceEmpty: { padding: 14, borderRadius: 14, border: `1px dashed ${c.cardBorder}`, color: c.textSoft, fontSize: 13 },
    leaveButton: { padding: "10px 14px", borderRadius: 12, border: `1px solid ${c.secondaryBtnBorder}`, background: c.secondaryBtnBg, color: c.secondaryBtnText, fontWeight: 800, cursor: "pointer" },
    leaveButtonGreen: { padding: "10px 14px", borderRadius: 12, border: "1px solid rgba(16,185,129,.55)", background: "rgba(16,185,129,.18)", color: "#34d399", fontWeight: 900, cursor: "pointer" },
    leaveForm: { display: "grid", gap: 12, padding: 18, borderBottom: `1px solid ${c.cardBorder}` },
    leaveDates: { display: "grid", gridTemplateColumns: isMobilePortrait ? "1fr" : "1fr 1fr", gap: 12 },
    leaveLabel: { display: "grid", gap: 6, color: c.textMain, fontSize: 13, fontWeight: 800 },
    leaveHint: { color: c.textSoft, fontWeight: 500 },
    leaveInput: { width: "100%", boxSizing: "border-box", borderRadius: 12, border: `1px solid ${c.cardBorder}`, padding: 12, background: c.secondaryBtnBg, color: c.textMain, font: "inherit", colorScheme: isLight ? "light" : "dark" },
    leaveFile: { color: c.textSoft, fontSize: 13 },
    leaveError: { color: "#ff8d9b", fontSize: 13, fontWeight: 700 },
    leaveItemButton: { width: "100%", border: "none", cursor: "pointer", color: "inherit", font: "inherit", background: "transparent" },
    leaveOpenHint: { marginTop: 6, color: c.accent, fontSize: isMobilePortrait ? 11 : 12, fontWeight: 800 },
    leaveDetailBackdrop: {
      position: "fixed", inset: 0, zIndex: 1400, background: "rgba(2,8,18,0.72)",
      display: "flex", alignItems: isMobilePortrait ? "flex-end" : "center", justifyContent: "center",
      padding: isMobilePortrait ? 0 : 24,
    },
    leaveDetailCard: {
      width: isMobilePortrait ? "100%" : "min(620px, 92vw)", maxHeight: isMobilePortrait ? "88vh" : "82vh",
      overflowY: "auto", background: c.cardBg, color: c.pageText, border: `1px solid ${c.cardBorder}`,
      borderRadius: isMobilePortrait ? "22px 22px 0 0" : 22, boxShadow: c.cardShadow,
      padding: isMobilePortrait ? 20 : 26,
    },
    leaveConfirmBackdrop: {
      position: "fixed", inset: 0, zIndex: 1500, background: "rgba(2,8,18,0.80)",
      display: "flex", alignItems: isMobilePortrait ? "flex-end" : "center", justifyContent: "center",
      padding: isMobilePortrait ? 0 : 24, backdropFilter: "blur(5px)", WebkitBackdropFilter: "blur(5px)",
    },
    leaveConfirmCard: {
      width: isMobilePortrait ? "100%" : "min(560px, 92vw)",
      background: c.cardBg, color: c.pageText, border: `1px solid ${isLight ? "rgba(15,23,42,.14)" : "rgba(96,165,250,.24)"}`,
      borderRadius: isMobilePortrait ? "24px 24px 0 0" : 24, boxShadow: "0 28px 80px rgba(0,0,0,.42)",
      padding: isMobilePortrait ? "20px 18px 24px" : 26,
    },
    leaveConfirmHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, marginBottom: 14 },
    leaveConfirmIcon: {
      width: 44, height: 44, borderRadius: 14, display: "grid", placeItems: "center",
      background: "rgba(244,63,94,.12)", border: "1px solid rgba(244,63,94,.38)",
      color: "#fb7185", fontSize: 22, fontWeight: 950,
    },
    leaveConfirmTitle: { marginTop: 6, color: c.title, fontSize: isMobilePortrait ? 24 : 28, lineHeight: 1.08, fontWeight: 950 },
    leaveConfirmMeta: { marginTop: 8, color: c.textSoft, fontSize: 13, fontWeight: 750 },
    leaveConfirmCopy: { marginTop: 16, color: c.pageText, fontSize: 14, lineHeight: 1.6 },
    leaveConfirmImpactGrid: { display: "grid", gridTemplateColumns: isMobilePortrait ? "1fr" : "repeat(3, 1fr)", gap: 10, marginTop: 18 },
    leaveConfirmImpact: {
      padding: 13, borderRadius: 14, border: `1px solid ${c.cardBorder}`,
      background: isLight ? "rgba(248,251,255,.94)" : "rgba(7,20,38,.78)",
      display: "grid", gap: 6, color: c.pageText, fontSize: 12, lineHeight: 1.35,
    },
    leaveConfirmImpactLabel: { color: c.accent, fontSize: 9, fontWeight: 950, letterSpacing: .8 },
    leaveConfirmActions: {
      display: "grid", gridTemplateColumns: isMobilePortrait ? "1fr" : "1fr 1.35fr", gap: 10, marginTop: 22,
    },
    leaveConfirmKeepBtn: {
      padding: "13px 16px", borderRadius: 14, border: `1px solid ${c.cardBorder}`,
      background: c.secondaryBtnBg, color: c.secondaryBtnText, fontWeight: 900, cursor: "pointer",
    },
    leaveConfirmCancelBtn: {
      padding: "13px 16px", borderRadius: 14, border: "1px solid rgba(244,63,94,.55)",
      background: "linear-gradient(135deg, rgba(225,29,72,.95), rgba(190,24,93,.92))",
      color: "#fff", fontWeight: 950, boxShadow: "0 10px 28px rgba(225,29,72,.20)",
    },
    leaveDetailHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 18, marginBottom: 20 },
    leaveDetailTitle: { marginTop: 6, fontSize: isMobilePortrait ? 24 : 30, lineHeight: 1.08, fontWeight: 900, color: c.title },
    leaveDetailClose: {
      width: 42, height: 42, borderRadius: 14, border: `1px solid ${c.cardBorder}`,
      background: "transparent", color: c.pageText, fontSize: 26, cursor: "pointer",
    },
    leaveDetailGrid: { display: "grid", gap: 14 },
    leaveDetailActions: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 16 },
    leaveCancelBtn: {
      padding: "11px 14px", borderRadius: 12, border: "1px solid rgba(244,63,94,.45)",
      background: "rgba(244,63,94,.10)", color: "#fb7185", fontWeight: 900, cursor: "pointer",
    },
    leaveDetailSection: {
      padding: 16, borderRadius: 16, border: `1px solid ${c.cardBorder}`,
      background: isLight ? "rgba(248,251,255,0.92)" : "rgba(7,20,38,0.72)",
    },
    leaveDetailBody: { marginTop: 8, color: c.pageText, lineHeight: 1.55, whiteSpace: "pre-wrap" },
    leaveEvidenceLink: { display: "inline-block", marginTop: 8, color: c.accent, fontWeight: 900, textDecoration: "none" },
    leaveList: { display: "grid" },
    leaveItem: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 18px", borderBottom: `1px solid ${c.cardBorder}` },
    leavePending: { borderRadius: 999, padding: "7px 11px", fontSize: 12, fontWeight: 900, background: "rgba(245,158,11,.14)", color: "#f59e0b" },
    leaveApproved: { borderRadius: 999, padding: "7px 11px", fontSize: 12, fontWeight: 900, background: "rgba(16,185,129,.14)", color: "#10b981" },
    leaveRejected: { borderRadius: 999, padding: "7px 11px", fontSize: 12, fontWeight: 900, background: "rgba(244,63,94,.14)", color: "#fb7185" },

    actionsRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12,
    },

    primaryBtn: {
      width: "100%",
      padding: 15,
      borderRadius: 16,
      border: "none",
      cursor: "pointer",
      fontWeight: 800,
      fontSize: 16,
      color: "#fff",
      background: c.primaryBtnBg,
      boxShadow: c.primaryBtnShadow,
    },

    secondaryBtn: {
      width: "100%",
      padding: 15,
      borderRadius: 16,
      border: `1px solid ${c.secondaryBtnBorder}`,
      cursor: "pointer",
      fontWeight: 800,
      fontSize: 16,
      color: c.secondaryBtnText,
      background: c.secondaryBtnBg,
      boxShadow: c.secondaryBtnShadow,
    },
  };

  return styles;
};
